import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Platform,
  TextInput,
  Modal,
  useWindowDimensions,
  type LayoutChangeEvent,
} from "react-native";
import { Trans, useTranslation } from "react-i18next";
import { ComponentStyles } from "../../../../constants/componentStyles";
import {
  useSessionStore,
  getPersistedSendCashStepsMode,
} from "../../../../../../state/src/useSessionStore";
import type {
  SessionState,
  SendCashStepsMode,
} from "../../../../../../state/src/useSessionStore";
import { useApiWithLoading } from "../../../../hooks/useApiWithLoading";
import { useVisualViewportHeight } from "../../../../hooks/useVisualViewportHeight";
import { useWebNarrowViewport } from "../../../../hooks/useWebNarrowViewport";
import { useWebVirtualKeyboardOpen } from "../../../../hooks/useWebVirtualKeyboardOpen";
import { postFeeLookup } from "../../../../services/common.service";
import { ProductType } from "../../../../view-models/productType";
// import { useGlobalError } from '../../../uiComponents/GlobalErrorProvider';
import { useGlobalError } from "../../../uiComponents/GlobalErrorProvider";

// Icons
import BackIcon from "../../../../../../../assets/images/BackIcon.svg";
import HelpIcon from "../../../../../../../assets/images/HelpIcon.svg";
import CloseIcon from "../../../../../../../assets/images/CloseIcon.svg";
import DownArrow from "../../../../../../../assets/images/DownArrowIcon.svg";
import AccountLockedIcon from "../../../../../../../assets/images/AccountLocked.svg";
import GroupId from "../../../../../../../assets/images/IconContainer.svg";
import WarninBlackIcon from "../../../../../../../assets/images/WarningBlackIcon.svg";
import {
  getFieldsForProductRequest,
  updateConsumerProfile,
  validateConsumer,
  postAuthConsumer,
} from "../../../../services/send.service";
import {
  buildSendValidationRequestFromStore,
  extractErrorDetailFromValidateConsumerResponse,
  flattenGffpFieldsByCategory,
  formatValidateConsumerErrorMessage,
  hasSendAdditionalInformationToCollect,
  isReqProductFieldVisibility,
  pruneSendAdditionalFieldValuesForMissingFields,
} from "../../../../helper/sendValidationHelper";

// Send flow step components
// import EnterPhoneStep, { EnterPhoneStepHandle } from "./steps/EnterPhoneStep";
import EnterPhoneStep, {
  EnterPhoneStepHandle,
} from "../../common/steps/EnterPhoneStep";
import EnterSenderNameStep, {
  SenderFullNameStepHandle,
} from "./steps/EnterSenderNameStep";
// import SenderAddressStep, { SenderAddressStepHandle }  from "./steps/SenderAddressStep";
import SenderAddressStepNew, {
  SenderAddressStepHandle,
} from "./steps/SenderAddressStepNew";
import SenderDateOfBirthStep, {
  SenderDateOfBirthStepHanle,
} from "./steps/SenderDateOfBirthStep";
import SenderIdentificationNumber, {
  SenderIdentificationStepHandle,
} from "./steps/SenderIdentificationNumber";
import ReceiverFullNameStep, {
  ReceiverFullNameStepHandle,
} from "./steps/ReceiverFullNameStep";
import PinInputStep from "./steps/PinInputStep";
import ConfirmPinStep from "./steps/ConfirmPinStep";
import OtpVerificationStep from "./steps/OtpVerificationStep";
import ChooseCountryStep from "./steps/ChooseCountryStep";
import ChooseStateStep from "./steps/ChooseStateStep";
import HowTheyReceiveStep from "./steps/HowTheyReceiveStep";
import SendingAmountEntryStep from "./steps/SendAmountEntryStep";
import ReviewAndEditStep, {
  ReviewAndEditStepHandle,
} from "./steps/ReviewAndEditStep";
import SenderIdentificationTypeStep from "./steps/SenderIdentificationTypeStep";
import ChooseStateOfIdStep from "./steps/ChooseStateOfIdStep";
import FraudPreventionStep from "../../common/steps/FraudPreventionStep";
import ReviewFinalStep from "./steps/ReviewFinalStep";
import ConfirmationStep from "../../common/steps/ConfirmationStep";
import HeadToCashierStep from "./steps/HeadToCashierStep";
import AdditionalInformationStep, {
  type AdditionalInformationStepHandle,
} from "./steps/AdditionalInformationStep";
import { GffpHelper } from "../../../../helper/gffpHelper";
import { FieldsForProductInfo } from "../../../../view-models/FieldsForProductInfo";
import { logUpdateConsumerProfileDraft } from "../../../../helper/createProfileHelper";
import { SendMoneyPostUpdateConsumerProfileParameter } from "../../../../view-models/SendMoneyPostUpdateConsumerProfileParameter";
import { buildAuthConsumerPostParameter } from "../../../../helper/authConsumerHelper";
import { AuthConsumerInfo } from "../../../../view-models/AuthConsumerInfo";
import { RequiredFields } from "../../../../view-models/RequiredFields";
import Colors from "../../../../constants/colors";

import { Flag } from "../../../Flag";
import NeedHelpComponent from "../../common/NeedHelpComponent";
import CalenderContainer from "../../../../../../../assets/images/CalendarContainer-2.svg";
import GradientBackground from "../../../uiComponents/GradientBackground";
import GradientExpandSwipeArea from "../../../uiComponents/GradientExpandSwipeArea";
import { FeeLookupHelper } from "../../../../helper/feeLookupHelper";
import type { FeeInfo } from "../../../../view-models/feeInfo";
import { capturePosthogEvent, trackUIError } from "../../../../utils/analytics";
import type { WebFlowRoutingProps } from "../../../../navigation/webFlowRouting";
import { useWebFlowStepSync } from "../../../../hooks/useWebFlowStepSync";

interface MainSendScreenProps {
  handleClose?: () => void;
  handleFinish?: () => void;
  /** Web: sync URL `/send/:stepSlug` with active step (optional; native omits). */
  webFlowRouting?: WebFlowRoutingProps;
}

/** identifications.json `key` for Mexican Electoral (spelling matches source data). */
const MEXICAN_ELECTORAL_ID_TYPE_KEY = "MexicanElectorialIdentification";
const PASSPORT_ID_TYPE_KEY = "Passport";

/** GFFP fields not collected in send UI (validation sends empty when product lists the tag). */
const GFFP_XML_TAGS_SKIPPED_SEND_UI = new Set(["customerreceivenumber"]);

function filterSendFlowMissingGffpFields(
  missing: any[] | undefined | null,
): any[] {
  if (!missing?.length) return missing || [];
  return missing.filter(
    (f) =>
      !GFFP_XML_TAGS_SKIPPED_SEND_UI.has(
        String(f?.xmlTag ?? f?.XmlTag ?? "").toLowerCase(),
      ),
  );
}

const BASE_STEPS_NEW = [
  { key: "phone", component: EnterPhoneStep },
  { key: "country", component: ChooseCountryStep },
  { key: "state", component: ChooseStateStep },
  { key: "sendingAmountEntry", component: SendingAmountEntryStep },
  { key: "howTheyReceive", component: HowTheyReceiveStep },
  { key: "reviewAndEditStep", component: ReviewAndEditStep },
  { key: "senderName", component: EnterSenderNameStep },
  { key: "senderAddress", component: SenderAddressStepNew },
  { key: "senderDOB", component: SenderDateOfBirthStep },
  { key: "senderIdentification", component: SenderIdentificationTypeStep },
  { key: "stateOfId", component: ChooseStateOfIdStep },
  { key: "senderIdNumber", component: SenderIdentificationNumber },
  { key: "receiverName", component: ReceiverFullNameStep },
  { key: "additionalInformation", component: AdditionalInformationStep },
  { key: "fraudPrevention", component: FraudPreventionStep },
  { key: "finalReview", component: ReviewFinalStep },
  { key: "pin", component: PinInputStep },
  { key: "confirmPin", component: ConfirmPinStep },
  { key: "pinCreated", component: ConfirmationStep },
  { key: "headToCashier", component: HeadToCashierStep },
];

const BASE_STEPS_EXISTING = [
  { key: "phone", component: EnterPhoneStep },
  { key: "pin", component: PinInputStep },
  { key: "country", component: ChooseCountryStep },
  { key: "state", component: ChooseStateStep },
  { key: "sendingAmountEntry", component: SendingAmountEntryStep },
  { key: "howTheyReceive", component: HowTheyReceiveStep },
  { key: "reviewAndEditStep", component: ReviewAndEditStep },
  { key: "senderName", component: EnterSenderNameStep },
  { key: "senderAddress", component: SenderAddressStepNew },
  { key: "senderDOB", component: SenderDateOfBirthStep },
  { key: "senderIdentification", component: SenderIdentificationTypeStep },
  { key: "stateOfId", component: ChooseStateOfIdStep },
  { key: "senderIdNumber", component: SenderIdentificationNumber },
  { key: "receiverName", component: ReceiverFullNameStep },
  { key: "additionalInformation", component: AdditionalInformationStep },
  { key: "fraudPrevention", component: FraudPreventionStep },
  { key: "finalReview", component: ReviewFinalStep },
  { key: "headToCashier", component: HeadToCashierStep },
];

const RESET_PIN_STEPS = [
  { key: "otp", component: OtpVerificationStep },
  { key: "pin", component: PinInputStep },
  { key: "confirmPin", component: ConfirmPinStep },
  { key: "pinCreated", component: ConfirmationStep },
];

/** PromotionDiscountId 7 = POBO (Payout Bonus): improved exchange/receive amount, not a send-fee discount. */
function promotionInfoIsPobo(promotionInfo: unknown): boolean {
  if (!Array.isArray(promotionInfo)) return false;
  return promotionInfo.some(
    (p: any) =>
      String(p?.PromotionDiscountId ?? p?.promotionDiscountId) === "7",
  );
}

/** Any PromotionErrorCode means the promo must not change fees or FX in the UI. */
function promotionInfoHasError(promotionInfo: unknown): boolean {
  if (!Array.isArray(promotionInfo)) return false;
  return promotionInfo.some(
    (p: any) => !!(p?.PromotionErrorCode || p?.promotionErrorCode),
  );
}

function hasDeliverySelectionIdentity(current: any): boolean {
  if (!current || typeof current !== "object") return false;
  return !!(
    current.DeliveryOption ||
    current.DeliveryOptId ||
    current.ReceiveAgentId ||
    current.ReceiveAgentID ||
    current.ReceiveAgentName ||
    current.DeliveryOptDisplayName
  );
}

/** Stable compare key for delivery edits (option + agent). */
function getSendDeliverySelectionKey(opt: any): string {
  if (!opt || typeof opt !== "object") return "";
  return [
    String(opt.DeliveryOption ?? opt.DeliveryOptId ?? "")
      .trim()
      .toUpperCase(),
    String(opt.ReceiveAgentId ?? opt.ReceiveAgentID ?? "").trim(),
  ].join("|");
}

function findMatchingFeeInfo(feeInfos: any[], current: any): any | null {
  if (!Array.isArray(feeInfos) || feeInfos.length === 0) return null;
  if (!hasDeliverySelectionIdentity(current)) return null;

  const match = feeInfos.find((f: any) => {
    const currentAgentId =
      current?.ReceiveAgentId || current?.ReceiveAgentID || null;
    const feeAgentId = f?.ReceiveAgentId || f?.ReceiveAgentID || null;
    if (currentAgentId && feeAgentId && currentAgentId === feeAgentId) {
      return true;
    }

    const currentDelivery = (
      current?.DeliveryOption ??
      current?.DeliveryOptId ??
      ""
    )
      .toString()
      .toUpperCase();
    const feeDelivery = (f?.DeliveryOption ?? f?.DeliveryOptId ?? "")
      .toString()
      .toUpperCase();

    if (!currentDelivery || !feeDelivery || currentDelivery !== feeDelivery) {
      return false;
    }

    if (
      currentDelivery === "WILL_CALL" ||
      currentDelivery === "WILLCALL_TO"
    ) {
      return (
        !!current?.DeliveryOptDisplayName &&
        f?.DeliveryOptDisplayName === current?.DeliveryOptDisplayName
      );
    }

    if (current?.ReceiveAgentName && f?.ReceiveAgentName) {
      return f?.ReceiveAgentName === current?.ReceiveAgentName;
    }

    return !currentAgentId && !feeAgentId;
  });

  return match ?? null;
}

function extractFeeInfos(parsed: any): any[] {
  return Array.isArray(parsed?.FeeInfos)
    ? parsed.FeeInfos
    : Array.isArray(parsed?.FeeInfo)
      ? parsed.FeeInfo
      : [];
}

function syncSendOptionFromFeeLookupMatch(
  match: any,
  setters: {
    setSelectedSendOption?: (opt: any) => void;
    setPendingSendOption?: (opt: any) => void;
    setTheyReceiveAmount?: (amount: string) => void;
    setSelectedReceiveCurrency?: (currency: string | null) => void;
  },
): any {
  const updatedOption = { ...match } as any;
  try {
    setters.setSelectedSendOption?.(updatedOption);
  } catch {}
  try {
    setters.setPendingSendOption?.(updatedOption);
  } catch {}

  const receiveAmount = Number(
    updatedOption?.ValidReceiveAmount ??
      updatedOption?.EstimatedReceiveAmount ??
      0,
  );
  const receiveCurrency = updatedOption?.ReceiveAmounts?.ValidCurrencyIndicator
    ? updatedOption?.ValidReceiveCurrency || ""
    : updatedOption?.EstimatedReceiveCurrency ||
      updatedOption?.ValidReceiveCurrency ||
      "";
  try {
    setters.setTheyReceiveAmount?.(
      Number.isFinite(receiveAmount) ? receiveAmount.toFixed(2) : "0.00",
    );
  } catch {}
  try {
    setters.setSelectedReceiveCurrency?.(receiveCurrency || null);
  } catch {}
  return updatedOption;
}

/** Prefer SendAmounts.TotalAmountToCollect — API often leaves root TotalAmount stale after fee/promo updates. */
function resolveTotalAmountToCollect(optionLike: any): number {
  const tac = optionLike?.SendAmounts?.TotalAmountToCollect;
  if (typeof tac === "number" && !Number.isNaN(tac)) {
    return tac;
  }
  const root = optionLike?.TotalAmount;
  if (typeof root === "number" && !Number.isNaN(root)) {
    return root;
  }
  return 0;
}

export const MainSendCashScreen: React.FC<MainSendScreenProps> = ({
  handleClose,
  handleFinish,
  webFlowRouting,
}) => {
  const { t } = useTranslation();
  const { executeWithLoading } = useApiWithLoading();
  const { showError } = useGlobalError();
  const [currentStep, setCurrentStep] = useState(0);

  const [steps, setSteps] = useState(() => {
    const mode = getPersistedSendCashStepsMode();
    if (mode === "new") return BASE_STEPS_NEW;
    if (mode === "resetPin") return RESET_PIN_STEPS;
    return BASE_STEPS_EXISTING;
  });
  // const [currentStep, setCurrentStep] = useState(0);

  const sendCashStepsMode = useSessionStore(
    (s: SessionState) => (s as SessionState).sendCashStepsMode ?? "existing",
  );
  const setSendCashStepsMode = useSessionStore(
    (s: SessionState) => (s as SessionState).setSendCashStepsMode,
  );

  /** Keep step component list aligned with persisted mode after reload (NEW vs EXISTING vs reset PIN). */
  useEffect(() => {
    const mode: SendCashStepsMode = sendCashStepsMode ?? "existing";
    if (mode === "new") setSteps(BASE_STEPS_NEW);
    else if (mode === "resetPin") setSteps(RESET_PIN_STEPS);
    else setSteps(BASE_STEPS_EXISTING);
  }, [sendCashStepsMode]);

  useWebFlowStepSync(webFlowRouting, steps, currentStep, setCurrentStep);

  const [returnToStep, setReturnToStep] = useState<string | null>(null);
  const [isStepValid, setIsStepValid] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [errors, setErrors] = useState({
    promoCode: "",
  });
  const [reviewStepApiError, setReviewStepApiError] = useState<string | null>(
    null,
  );
  const [finalReviewApiError, setFinalReviewApiError] = useState<string | null>(
    null,
  );
  const [pinStepApiError, setPinStepApiError] = useState<string | null>(null);
  const [confirmPinApiError, setConfirmPinApiError] = useState<string | null>(
    null,
  );
  const phoneStepRef = React.useRef<EnterPhoneStepHandle | null>(null);
  const SenderAddressStepRef = React.useRef<SenderAddressStepHandle | null>(
    null,
  );
  const SenderFullNameStepRef = React.useRef<SenderFullNameStepHandle | null>(
    null,
  );
  const SenderDOBStepRef = React.useRef<SenderDateOfBirthStepHanle | null>(
    null,
  );
  const SenderIdentificationStepRef =
    React.useRef<SenderIdentificationStepHandle | null>(null);
  const ReceiverFullNameStepRef =
    React.useRef<ReceiverFullNameStepHandle | null>(null);
  const ReviewAndEditStepRef = React.useRef<ReviewAndEditStepHandle | null>(
    null,
  );
  const AdditionalInformationStepRef =
    React.useRef<AdditionalInformationStepHandle | null>(null);
  // Pin Verification
  // const [pin, setPin] = useState(["", "", "", ""]);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [locked, setLocked] = useState(false);
  const [showPinAttemptsWarning, setShowPinAttemptsWarning] = useState(false);
  const pinErrorResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [showPopup, setShowPopup] = useState(false);
  const [needHelp, setNeedHelp] = useState(false);
  const [isDOBConfirm, setIsDOBCOnfirm] = useState(false);
  const [isReceiverFullnameConfirm, setIsReceiverFullnameConfirm] =
    useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const [isGradientExpand, setIsGradientExpand] = useState(false);
  /** Web + reviewAndEditStep only: while promo (or other) field is focused, gradient footer is in-flow in ScrollView instead of `position: fixed`. */
  const [reviewWebFieldFocused, setReviewWebFieldFocused] = useState(false);
  const sendReviewGradientStepKeys = useMemo(
    () => new Set<string>(["reviewAndEditStep", "finalReview"]),
    [],
  );
  /**
   * Send steps that keep Continue stuck just above the keyboard (web + native).
   * On web, the root shell height follows `visualViewport` for these keys so the footer
   * sits at the bottom of the visible area without an artificial spacer; the step's own
   * ScrollView (e.g. ReceiverFullNameStep) handles overflow if inputs
   * don't fit. Single-field steps like senderDOB and senderIdNumber scroll via
   * the parent ScrollView in MainSendCashScreen. All other steps use full window
   * height so the footer stays pinned at the page bottom while the user scrolls
   * multi-field forms.   */
  const sendStickyKeyboardContinueKeys = useMemo(
    () =>
      new Set<string>(["phone", "senderDOB", "senderIdNumber", "receiverName"]),
    [],
  );
  const sendCurrentStepKey = steps[currentStep]?.key;
  const stickFooterAboveKeyboard = sendStickyKeyboardContinueKeys.has(
    sendCurrentStepKey ?? "",
  );
  useEffect(() => {
    if (
      sendCurrentStepKey &&
      sendReviewGradientStepKeys.has(sendCurrentStepKey)
    ) {
      setIsGradientExpand(true);
    } else {
      setIsGradientExpand(false);
    }
  }, [sendCurrentStepKey, sendReviewGradientStepKeys]);

  useEffect(() => {
    if (sendCurrentStepKey !== "reviewAndEditStep") {
      setReviewWebFieldFocused(false);
    }
  }, [sendCurrentStepKey]);

  const { height: screenHeight } = useWindowDimensions();
  /** Web (esp. iOS Safari): sync layout height with visual viewport when URL bar shows/hides. */
  const webVisualViewportHeight = useVisualViewportHeight();
  /** Touch-primary mobile web (not mouse desktop); gates review gradient in-scroll footer. */
  const isWebNarrowViewport = useWebNarrowViewport();
  /** `visualViewport` overlap + hysteresis — IME open/closed on mobile web (no OS-specific keyboard events exist). */
  const reviewWebVk = useWebVirtualKeyboardOpen(
    Platform.OS === "web" &&
      isWebNarrowViewport &&
      sendCurrentStepKey === "reviewAndEditStep",
  );
  const reviewWebVkOpen = reviewWebVk.keyboardOpen;
  const reviewWebVkOverlapPx = reviewWebVk.overlapPx;
  const reviewWebKeyboardOpenPrevRef = useRef(false);
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const prev = reviewWebKeyboardOpenPrevRef.current;
    reviewWebKeyboardOpenPrevRef.current = reviewWebVkOpen;
    /** Viewport + overlap: stricter “keyboard gone” than hysteresis alone (helps Chrome checkmark). */
    const overlapMostlyGone = reviewWebVkOverlapPx < 36;
    if (prev && !reviewWebVkOpen && overlapMostlyGone) {
      setReviewWebFieldFocused(false);
    }
  }, [reviewWebVkOpen, reviewWebVkOverlapPx]);

  useEffect(() => {
    if (Platform.OS === "web" && !isWebNarrowViewport) {
      setReviewWebFieldFocused(false);
    }
  }, [isWebNarrowViewport]);

  const layoutHeight =
    Platform.OS === "web"
      ? (webVisualViewportHeight ?? screenHeight)
      : screenHeight;
  /**
   * Web (iOS Safari): single-field steps shrink with `visualViewport` so Continue sits above the keyboard.
   * Multi-field steps keep full window height so the footer stays at the bottom (scroll the form; keyboard may cover the lower area).
   */
  const effectiveLayoutHeight =
    Platform.OS === "web"
      ? stickFooterAboveKeyboard
        ? layoutHeight
        : screenHeight
      : layoutHeight;
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  const inputs = useRef<(TextInput | null)[]>([]);
  /** When true, receiverName → fraudPrevention skipped additionalInformation (matches forward path). Used so Back from fraud does not show AI. */
  const skippedAdditionalInformationBeforeFraudRef = useRef(false);
  /** How we entered additionalInformation, so Back goes to the right step (not linear receiverName when from finalReview). */
  const additionalInformationNavSourceRef = useRef<
    "receiverReturnToFraud" | "finalReview" | null
  >(null);
  const setConsumerPin = useSessionStore((s: any) => s.setConsumerPin);

  const selectedDestinationCountryCode = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryCode,
  );
  const selectedCountryName = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryName,
  );
  const selectedDestinationStateAbbr = useSessionStore(
    (s: SessionState) => s.selectedDestinationStateAbbr,
  );
  const pendingSendOption = useSessionStore(
    (s: SessionState) => (s as any).pendingSendOption,
  );
  const selectedSendOptionValue = useSessionStore(
    (s: SessionState) => (s as any).selectedSendOption,
  );
  const setSelectedSendOption = useSessionStore(
    (s: SessionState) => (s as any).setSelectedSendOption,
  );
  const currentSendAmount = useSessionStore(
    (s: SessionState) => (s as any).currentSendAmount,
  );
  const theyReceiveAmount = useSessionStore(
    (s: SessionState) => (s as any).theyReceiveAmount,
  );
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const senderDOB = useSessionStore((s: SessionState) => s.senderDOB);
  const senderDOBStringFromStore = useSessionStore(
    (s: any) => (s as any).senderDOBString,
  );
  const selectedReceiveCurrency = useSessionStore(
    (s: SessionState) => s.selectedReceiveCurrency,
  );
  const setLastFeeLookupResponse = useSessionStore(
    (s: SessionState) => (s as any).setLastFeeLookupResponse,
  );
  const setSendFeeLookupReviewEditStore = useSessionStore(
    (s: SessionState) => (s as any).setSendFeeLookupReviewEditStore,
  );
  const setGffpCategories = useSessionStore(
    (s: SessionState) => (s as any).setGffpCategories,
  );
  const setGffpFieldsByCategory = useSessionStore(
    (s: SessionState) => (s as any).setGffpFieldsByCategory,
  );
  const setMissingRequiredFields = useSessionStore(
    (s: SessionState) => (s as any).setMissingRequiredFields,
  );
  const additionalFieldValues = useSessionStore(
    (s: SessionState) => (s as any).additionalFieldValues,
  );
  const selectedIdPhotoIdCountry = useSessionStore(
    (s: SessionState) => (s as any).selectedIdPhotoIdCountry,
  );
  const recordStepTransition = useSessionStore(
    (s: SessionState) => (s as any).recordStepTransition,
  );
  const isNewConsumer = useSessionStore(
    (s: SessionState) => (s as any).isNewConsumer,
  );
  // Sender profile setters to hydrate from AuthConsumer
  const setConsumerId = useSessionStore(
    (s: SessionState) => (s as any).setConsumerId,
  );
  const setSenderFirstName = useSessionStore(
    (s: SessionState) => (s as any).setSenderFirstName,
  );
  const setSenderMiddleName = useSessionStore(
    (s: SessionState) => (s as any).setSenderMiddleName,
  );
  const setSenderLastName = useSessionStore(
    (s: SessionState) => (s as any).setSenderLastName,
  );
  const setSenderAddress = useSessionStore(
    (s: SessionState) => (s as any).setSenderAddress,
  );
  const setSenderCity = useSessionStore(
    (s: SessionState) => (s as any).setSenderCity,
  );
  const setSenderState = useSessionStore(
    (s: SessionState) => (s as any).setSenderState,
  );
  const setSenderCountry = useSessionStore(
    (s: SessionState) => (s as any).setSenderCountry,
  );
  const setSenderZipCode = useSessionStore(
    (s: SessionState) => (s as any).setSenderZipCode,
  );
  const setSenderDOBString = useSessionStore(
    (s: SessionState) => (s as any).setSenderDOBString,
  );
  const setSenderDOB = useSessionStore(
    (s: SessionState) => (s as any).setSenderDOB,
  );
  const setPhoneNumber = useSessionStore(
    (s: SessionState) => (s as any).setPhoneNumber,
  );
  const setConfirmationNumber = useSessionStore(
    (s: SessionState) => (s as any).setConfirmationNumber,
  );
  const setFinalSendValidationResponseStore = useSessionStore(
    (s: SessionState) => (s as any).setFinalSendValidationResponseStore,
  );
  const consumerIdFromStore = useSessionStore(
    (s: SessionState) => (s as any).consumerId,
  );
  const StepComponent = steps[currentStep].component;
  const AnyStepComponent = StepComponent as any;
  const setIsNewConsumer = useSessionStore(
    (s: SessionState) => (s as any).setIsNewConsumer,
  );
  const setIsPromocodeApplied = useSessionStore(
    (s: SessionState) => (s as any).setIsPromocodeApplied,
  );
  const setIsPromoActivityIndicator = useSessionStore(
    (s: SessionState) => (s as any).setIsPromoActivityIndicator,
  );
  const receiverFirstName = useSessionStore((s: any) => s.receiverFirstName);
  const receiverLastName = useSessionStore((s: any) => s.receiverLastName);
  const setSelectedSendOptionStore = useSessionStore(
    (s: SessionState) => (s as any).setSelectedSendOption,
  );
  const setPendingSendOptionStore = useSessionStore(
    (s: SessionState) => (s as any).setPendingSendOption,
  );
  const setTheyReceiveAmount = useSessionStore(
    (s: SessionState) => (s as any).setTheyReceiveAmount,
  );
  const setSelectedReceiveCurrency = useSessionStore(
    (s: SessionState) => s.setSelectedReceiveCurrency,
  );
  const setSelectedDeliveryOptionStore = useSessionStore(
    (s: SessionState) => (s as any).setSelectedDeliveryOptionStore,
  );
  const lastFeeLookupResponse = useSessionStore(
    (s: SessionState) => (s as any).lastFeeLookupResponse,
  );
  const selectedDeliveryOptionStore = useSessionStore(
    (s: SessionState) => (s as any).selectedDeliveryOptionStore,
  );
  const selectedSendOption = useSessionStore(
    (s: SessionState) => (s as any).selectedSendOption,
  );
  const [preApplyFees, setPreApplyFees] = useState<number | null>(null);
  const [postApplyFees, setPostApplyFees] = useState<number | null>(null);
  /** Exchange rate before promo fee lookup; used to strikethrough old rate for POBO (Payout Bonus) only. */
  const [preApplyExchangeRate, setPreApplyExchangeRate] = useState<
    number | null
  >(null);
  const feeLookupParamsFromStore = useSessionStore(
    (s: SessionState) => (s as any).sendFeeLookupReviewEditStore,
  );
  const isPromocodeApplied = useSessionStore(
    (s: SessionState) => (s as any).isPromocodeApplied,
  );
  // capturing below details in posthog events
  const senderFirstName = useSessionStore(
    (s: SessionState) => (s as any).senderFirstName,
  );
  const senderMiddleName = useSessionStore(
    (s: SessionState) => (s as any).senderMiddleName,
  );
  const senderLastName = useSessionStore(
    (s: SessionState) => (s as any).senderLastName,
  );
  const senderAddress = useSessionStore(
    (s: SessionState) => (s as any).senderAddress,
  );
  const senderCity = useSessionStore(
    (s: SessionState) => (s as any).senderCity,
  );
  const senderZipCode = useSessionStore(
    (s: SessionState) => (s as any).senderZipCode,
  );
  const senderState = useSessionStore(
    (s: SessionState) => (s as any).senderState,
  );
  const storedDobString = useSessionStore(
    (s: SessionState) => (s as any).senderDOBString,
  );
  const senderPhotoIdNumber = useSessionStore(
    (s: SessionState) => (s as any).senderPhotoIdNumber,
  );
  const storeFirst = useSessionStore((s: any) => s.receiverFirstName);
  const storeMiddle = useSessionStore((s: any) => s.receiverMiddleName);
  const storeLast = useSessionStore((s: any) => s.receiverLastName);
  const storeLast2 = useSessionStore((s: any) => s.receiverLastName2);
  const consumerPin = useSessionStore((s: any) => s.consumerPin);

  const option: any = pendingSendOption || selectedSendOption || null;

  const selectedDeliveryFeeInfo: FeeInfo | undefined = useMemo(() => {
    const arr: FeeInfo[] = Array.isArray(
      (lastFeeLookupResponse as any)?.FeeInfos,
    )
      ? (lastFeeLookupResponse as any).FeeInfos
      : Array.isArray((lastFeeLookupResponse as any)?.FeeInfo)
        ? (lastFeeLookupResponse as any).FeeInfo
        : [];
    const sel = ((selectedDeliveryOptionStore as any)?.DeliveryOption ?? "")
      .toString()
      .toUpperCase();
    if (!arr || arr.length === 0) return undefined;
    if (!sel) return arr[0];
    return (
      arr.find((f: any) => {
        const opt = (f?.DeliveryOption ?? f?.DeliveryOptId ?? "")
          .toString()
          .toUpperCase();
        return !!opt && opt === sel;
      }) || arr[0]
    );
  }, [lastFeeLookupResponse, selectedDeliveryOptionStore]);

  const summary = useMemo(() => {
    if (!option) {
      return {
        sendAmount: 0,
        sendCurrency: "USD",
        receiveAmount: 0,
        receiveCurrency: "—",
        exchangeRate: 0,
        fees: 0,
        total: 0,
      };
    }
    const sendCurrency: string = option?.SendAmounts?.SendCurrency || "USD";
    const sendAmount: number = Number(option?.SendAmounts?.SendAmount || 0);
    const receiveCurrency: string = option?.ReceiveAmounts
      ?.ValidCurrencyIndicator
      ? option?.ValidReceiveCurrency || ""
      : option?.EstimatedReceiveCurrency || option?.ValidReceiveCurrency || "";
    const receiveAmount: number = Number(
      option?.ValidReceiveAmount ?? option?.EstimatedReceiveAmount ?? 0,
    );
    const exchangeRateNum: number = Number(
      FeeLookupHelper.getExchangeRate(option) || 0,
    );
    const fees: number = Number(option?.SendAmounts?.TotalSendFees || 0);
    const total: number = resolveTotalAmountToCollect(option);
    return {
      sendAmount,
      sendCurrency,
      receiveAmount,
      receiveCurrency,
      exchangeRate: exchangeRateNum,
      fees,
      total,
    };
  }, [pendingSendOption, selectedSendOption]);

  const scrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    // scroll to top on step change
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    });
  }, [currentStep]);

  const updatePromocode = (promo: string) => {
    setPromoCode(promo);
    setErrors((prev) => ({ ...prev, promoCode: "" }));
  };

  const clearPromoCode = async () => {
    setPreApplyExchangeRate(null);
    if (!isPromocodeApplied) {
      setPromoCode("");
      setPreApplyFees(null);
      setPostApplyFees(null);
      return;
    }

    setIsPromoActivityIndicator(true);
    try {
      const storeParams = (feeLookupParamsFromStore || {}) as Record<
        string,
        unknown
      >;
      const { promoCodes: _omitPromo, ...restParams } = storeParams as any;
      const refreshedParams = {
        ...restParams,
        deliveryOption:
          (selectedDeliveryOptionStore as any)?.DeliveryOption ?? undefined,
        allOptions: false,
      } as any;

      try {
        setSendFeeLookupReviewEditStore?.(refreshedParams);
      } catch {}

      const feeResp = await postFeeLookup(refreshedParams as any);
      const rawData: any =
        feeResp && (feeResp as any).data !== undefined
          ? (feeResp as any).data
          : feeResp;
      let parsed: any = rawData;
      if (typeof rawData === "string") {
        try {
          parsed = JSON.parse(rawData);
        } catch {}
      }

      const errDetail = parsed?.ErrorDetail || parsed?.errorDetail;
      if (errDetail) {
        const errString =
          errDetail?.ErrorString ??
          errDetail?.errorString ??
          t("ERROR_AN_ERROR_OCCURED");
        showError(String(errString));
        return;
      }

      try {
        setLastFeeLookupResponse?.(parsed);
      } catch {}

      const feeInfos: any[] = Array.isArray(parsed?.FeeInfos)
        ? parsed?.FeeInfos
        : Array.isArray(parsed?.FeeInfo)
          ? parsed?.FeeInfo
          : [];

      const s = useSessionStore.getState() as any;
      const currentOpt =
        s.pendingSendOption || s.selectedSendOption || option || {};
      const match = findMatchingFeeInfo(feeInfos, currentOpt);

      if (!match) {
        showError(t("ERROR_INVALID_PROMOCODE"));
        return;
      }

      const restoredOption = { ...match } as any;
      try {
        setSelectedSendOptionStore?.(restoredOption);
      } catch {}
      try {
        setPendingSendOptionStore?.(restoredOption);
      } catch {}

      setPromoCode("");
      setIsPromocodeApplied(false);
      setPreApplyFees(null);
      setPostApplyFees(null);
    } catch (e: any) {
      console.warn("clearPromoCode fee lookup error:", e);
      try {
        showError(e?.message || "An error occurred refreshing fees.");
      } catch {}
    } finally {
      setIsPromoActivityIndicator(false);
    }
  };

  const handleApplyPromoCode = async () => {
    const trimmedPromoCode = promoCode.trim();
    const invalidPromoCodeMessage = t("ERROR_ENTER_A_VALID_PROMOCODE");
    // Validation
    if (!trimmedPromoCode) {
      setErrors((prev) => ({
        ...prev,
        promoCode: t("ERROR_ENTER_A_VALID_PROMOCODE"),
      }));
      return;
    }
    setPromoCode(trimmedPromoCode);
    setErrors((prev) => ({ ...prev, promoCode: "" }));
    setIsPromoActivityIndicator(true);
    setPreApplyFees(summary.fees);
    const optForPreRate =
      (useSessionStore.getState() as any).pendingSendOption ||
      (useSessionStore.getState() as any).selectedSendOption ||
      option;
    const prePromoRate = FeeLookupHelper.getExchangeRate(optForPreRate as any);
    setPreApplyExchangeRate(
      typeof prePromoRate === "number" && !Number.isNaN(prePromoRate)
        ? prePromoRate
        : null,
    );
    try {
      const updatedParams = {
        ...(feeLookupParamsFromStore || {}),
        promoCodes: trimmedPromoCode ? [trimmedPromoCode] : undefined,
        deliveryOption:
          (selectedDeliveryOptionStore as any)?.DeliveryOption ?? undefined,
        allOptions: false,
      } as any;

      try {
        setSendFeeLookupReviewEditStore?.(updatedParams);
      } catch {}

      const feeResp = await postFeeLookup(updatedParams as any);

      // Normalize response
      const rawData: any =
        feeResp && (feeResp as any).data !== undefined
          ? (feeResp as any).data
          : feeResp;
      let parsed: any = rawData;
      if (typeof rawData === "string") {
        try {
          parsed = JSON.parse(rawData);
        } catch {}
      }

      // Inspect potential error envelope in fee lookup response
      const errDetail = parsed?.ErrorDetail || parsed?.errorDetail;
      if (errDetail) {
        const errCode =
          errDetail?.ErrorCode ?? errDetail?.errorCode ?? "UNKNOWN";
        const errString =
          errDetail?.ErrorString ??
          errDetail?.errorString ??
          t("ERROR_AN_ERROR_OCCURED");
        trackUIError(errCode + " " + errString, t("ERROR_PROMOCODE"));
        setErrors((prev) => ({ ...prev, promoCode: invalidPromoCodeMessage }));
        setPreApplyFees(null);
        setPostApplyFees(null);
        setPreApplyExchangeRate(null);
        try {
          setIsPromocodeApplied(false);
        } catch {}
        return;
      }

      try {
        setLastFeeLookupResponse?.(parsed);
      } catch {}

      const feeInfos: any[] = Array.isArray(parsed?.FeeInfos)
        ? parsed?.FeeInfos
        : Array.isArray(parsed?.FeeInfo)
          ? parsed?.FeeInfo
          : [];

      const current = option || {};
      const match = findMatchingFeeInfo(feeInfos, current);

      if (!match) {
        setErrors((prev) => ({ ...prev, promoCode: invalidPromoCodeMessage }));
        setPreApplyFees(null);
        setPostApplyFees(null);
        setPreApplyExchangeRate(null);
        try {
          setIsPromocodeApplied(false);
        } catch {}
        return;
      }

      // Check promotion status
      const promoArray: any[] = Array.isArray(match?.PromotionInfo)
        ? match?.PromotionInfo
        : [];
      const hasError = promotionInfoHasError(promoArray);
      const isPoboPromo = promotionInfoIsPobo(promoArray);

      if (hasError) {
        setPreApplyFees(null);
        setPostApplyFees(null);
        setPreApplyExchangeRate(null);
        try {
          setIsPromocodeApplied(false);
        } catch {}
        setErrors((prev) => ({ ...prev, promoCode: invalidPromoCodeMessage }));
        return;
      }

      if (isPoboPromo) {
        // POBO: server improves exchange/receive only — keep fee fields as returned; do not apply client-side fee discount math.
        const updatedOption = { ...match } as any;
        try {
          setPostApplyFees(null);
        } catch {}
        try {
          setSelectedSendOptionStore?.(updatedOption);
        } catch {}
        try {
          setPendingSendOptionStore?.(updatedOption);
        } catch {}
      } else {
        setPreApplyExchangeRate(null);
        // Apply discount to fees if necessary (prefer server-calculated values)
        const discountAmt =
          Number(match?.SendAmounts?.TotalDiscountAmount ?? 0) ||
          Number(
            promoArray?.reduce(
              (sum: number, p: any) =>
                sum + Number(p?.PromotionDiscountAmount || 0),
              0,
            ),
          );
        const originalFees = Number(match?.SendAmounts?.TotalSendFees ?? 0);
        const taxes = Number(match?.SendAmounts?.TotalSendTaxes ?? 0);
        const sendAmt = Number(match?.SendAmounts?.SendAmount ?? 0);
        const discountedFees = Math.max(0, originalFees - (discountAmt || 0));

        const totalAmountToCollect =
          typeof match?.SendAmounts?.TotalAmountToCollect === "number"
            ? Number(match?.SendAmounts?.TotalAmountToCollect)
            : sendAmt + discountedFees + taxes;

        const updatedOption = {
          ...match,
          SendAmounts: {
            ...(match?.SendAmounts || {}),
            TotalDiscountAmount:
              discountAmt || match?.SendAmounts?.TotalDiscountAmount,
            TotalSendFees: discountedFees,
            TotalAmountToCollect: totalAmountToCollect,
          },
          TotalAmount: totalAmountToCollect,
        } as any;

        // Capture new fees (discount + discountedFees) for display
        try {
          setPostApplyFees(Number(match?.SendAmounts?.TotalSendFees));
        } catch {}
        try {
          setSelectedSendOptionStore?.(updatedOption);
        } catch {}
        try {
          setPendingSendOptionStore?.(updatedOption);
        } catch {}
      }
      try {
        setIsPromocodeApplied(true);
      } catch {}
      setErrors((prev) => ({ ...prev, promoCode: "" }));
    } catch (e: any) {
      setErrors((prev) => ({ ...prev, promoCode: invalidPromoCodeMessage }));
      setPreApplyFees(null);
      setPostApplyFees(null);
      setPreApplyExchangeRate(null);
      try {
        setIsPromocodeApplied(false);
      } catch {}
      console.warn("handleApplyPromoCode error:", e);
    } finally {
      setIsPromoActivityIndicator(false);
    }
  };

  // initialize timing for first step
  useEffect(() => {
    try {
      recordStepTransition?.(steps[0].key);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback each step can use to report validity
  const handleValidationChange = (valid: boolean) => {
    setIsStepValid(valid);
  };

  const clearStaleDeliverySelection = () => {
    try {
      setSelectedSendOptionStore?.(null);
    } catch {}
    try {
      setPendingSendOptionStore?.(null);
    } catch {}
    try {
      setSelectedDeliveryOptionStore?.(null);
    } catch {}
  };

  const syncSendAdditionalFieldsAfterDeliveryChange = async (options?: {
    invalidateCachedReqFields?: boolean;
  }): Promise<any[] | null> => {
    try {
      const sessionSnap = useSessionStore.getState() as any;
      const opt: any =
        sessionSnap.selectedSendOption || sessionSnap.pendingSendOption || {};
      const amountFromState = Number(
        String(currentSendAmount || "0").replace(/,/g, ""),
      );
      const amountFromOption = Number(opt?.SendAmounts?.SendAmount ?? 0);
      const payload: any = {
        unitProfileID: unitProfileId || "",
        agentID: agentId || "",
        receiveCountry:
          selectedDestinationCountryCode || opt?.DestinationCountry || "",
        deliveryOption: opt?.DeliveryOption || "",
        thirdPartyType: "NONE",
        receiveCurrency:
          selectedReceiveCurrency ||
          opt?.ValidReceiveCurrency ||
          opt?.EstimatedReceiveCurrency ||
          "",
        amount:
          isFinite(amountFromOption) && amountFromOption > 0
            ? amountFromOption
            : isFinite(amountFromState)
              ? amountFromState
              : 0,
        sendCurrency: opt?.SendAmounts?.SendCurrency || "USD",
        productType: ProductType.SEND,
        CustomerId: "0",
        receiveAgentID:
          opt?.ReceiveAgentId ??
          (opt as any)?.receiveAgentID ??
          opt?.ReceiveAgentID ??
          undefined,
      };
      const gffpResponse: FieldsForProductInfo =
        await getFieldsForProductRequest(payload);
      const requiredFields: any[] =
        GffpHelper.filterRequiredAndOptionalFields(gffpResponse) as any[];

      const groupedByCategory: Record<string, any[]> = requiredFields.reduce(
        (acc: Record<string, any[]>, field: any) => {
          const category =
            field.FieldCategory ?? field.fieldCategory ?? "Uncategorized";
          if (!acc[category]) acc[category] = [];
          acc[category].push(field);
          return acc;
        },
        {},
      );

      Object.keys(groupedByCategory).forEach((cat) => {
        groupedByCategory[cat].sort((a, b) => {
          const ao = a.DisplayOrder ?? a.displayOrder ?? 0;
          const bo = b.DisplayOrder ?? b.displayOrder ?? 0;
          return ao - bo;
        });
      });

      try {
        setGffpFieldsByCategory?.(groupedByCategory);
      } catch {}
      try {
        setGffpCategories?.(Object.keys(groupedByCategory));
      } catch {}

      if (options?.invalidateCachedReqFields) {
        const setAdditionalFieldValues =
          sessionSnap.setAdditionalFieldValues as
            | ((values: Record<string, any>) => void)
            | undefined;
        const currentExtras = {
          ...((sessionSnap.additionalFieldValues || {}) as Record<string, any>),
        };
        let changed = false;
        for (const category of Object.values(groupedByCategory)) {
          for (const field of category) {
            if (
              !isReqProductFieldVisibility(
                field?.visibility ?? field?.Visibility,
              )
            ) {
              continue;
            }
            const tag = String(field?.XmlTag ?? field?.xmlTag ?? "").trim();
            if (tag && tag in currentExtras) {
              delete currentExtras[tag];
              changed = true;
            }
          }
        }
        if (changed && setAdditionalFieldValues) {
          setAdditionalFieldValues(currentExtras);
        }
      }

      const consumerId = consumerIdFromStore
        ? String(consumerIdFromStore)
        : "0";
      const validationReq = buildSendValidationRequestFromStore(consumerId);
      const missing = filterSendFlowMissingGffpFields(
        new RequiredFields().getMissingFields(
          groupedByCategory,
          validationReq,
        ),
      );
      try {
        setMissingRequiredFields?.(missing as any);
      } catch {}
      pruneSendAdditionalFieldValuesForMissingFields(missing);
      return missing || [];
    } catch (e) {
      console.warn(
        "Failed to sync additional fields after delivery change",
        e,
      );
      return null;
    }
  };

  const handleEditStep = (key: string) => {
    if (key === "country") {
      const targetIndex = steps.findIndex((s) => s.key === "country");
      if (targetIndex !== -1) {
        const fromKey = steps[currentStep].key;
        if (fromKey === "reviewAndEditStep" || fromKey === "finalReview") {
          setReturnToStep(fromKey);
        }
        setCurrentStep(targetIndex);
      }
    } else {
      const targetIndex = steps.findIndex((s) => s.key === key);
      if (targetIndex !== -1) {
        setReturnToStep(steps[currentStep].key);
        setCurrentStep(targetIndex);
      }
    }
  };

  const handlePinVerification = async () => {
    // replace with real validation
    const invalidPinMessage = t("ERROR_INCORRECT_PIN");
    const handlePinVerificationFailure = () => {
      if (pinErrorResetTimeoutRef.current) {
        clearTimeout(pinErrorResetTimeoutRef.current);
        pinErrorResetTimeoutRef.current = null;
      }
      setPinStepApiError(invalidPinMessage);
      setShowPinAttemptsWarning(true);
      const remaining = attemptsLeft - 1;
      setAttemptsLeft(remaining);
      if (remaining <= 0) {
        setLocked(true);
        setShowPopup(true);
        setResetSignal((prev) => prev + 1);
        setConsumerPin("");
        inputs.current[0]?.focus();
        return;
      }
      pinErrorResetTimeoutRef.current = setTimeout(() => {
        setPinStepApiError(null);
        setResetSignal((prev) => prev + 1);
        setConsumerPin("");
        inputs.current[0]?.focus();
        pinErrorResetTimeoutRef.current = null;
      }, 350);
    };

    try {
      const authConsumerPostParameter = buildAuthConsumerPostParameter();
      const authConsumerResponse: AuthConsumerInfo = await postAuthConsumer(
        authConsumerPostParameter,
      );
      const respAny: any = authConsumerResponse as any;
      if (respAny?.ErrorDetail || respAny?.errorDetail) {
        handlePinVerificationFailure();
        return; // stay on PIN step when auth returns an error detail
      }
      // Hydrate sender profile details into the store from successful AuthConsumer
      const ci: any = respAny?.ConsumerInfo ?? respAny?.consumerInfo;
      if (ci) {
        // Consumer ID
        try {
          setConsumerId?.(String(ci.ConsumerId ?? ci.consumerId ?? ""));
        } catch {}
        try {
          setSenderFirstName?.(
            ci.SenderFirstName ?? ci.senderFirstName ?? null,
          );
        } catch {}
        try {
          setSenderMiddleName?.(
            ci.SenderMiddleName ?? ci.senderMiddleName ?? null,
          );
        } catch {}
        try {
          setSenderLastName?.(ci.SenderLastName ?? ci.senderLastName ?? null);
        } catch {}
        try {
          setSenderAddress?.(ci.SenderAddress ?? ci.senderAddress ?? null);
        } catch {}
        try {
          setSenderCity?.(ci.SenderCity ?? ci.senderCity ?? null);
        } catch {}
        try {
          setSenderState?.(ci.SenderState ?? ci.senderState ?? null);
        } catch {}
        try {
          const countryRaw = ci.SenderCountry ?? ci.senderCountry ?? null;
          const countryCode =
            countryRaw != null && String(countryRaw).trim()
              ? String(countryRaw).trim().toUpperCase()
              : null;
          setSenderCountry?.(countryCode);
        } catch {}
        try {
          setSenderZipCode?.(ci.SenderZipCode ?? ci.senderZipCode ?? null);
        } catch {}
        // Phone
        try {
          setPhoneNumber?.(ci.SenderHomePhone ?? ci.senderHomePhone ?? null);
        } catch {}
        // DOB
        const dobRaw: any = ci.SenderDOB ?? ci.senderDOB ?? null;
        if (dobRaw) {
          const dobStr = String(dobRaw).split("T")[0]; // handle YYYY-MM-DD or YYYY-MM-DDThh:mm:ss
          try {
            setSenderDOBString?.(dobStr || null);
          } catch {}
          // Parse YYYY-MM-DD at noon local time to avoid timezone shifts
          // This prevents the date from showing one day less in the confirmation modal
          if (/^\d{4}-\d{2}-\d{2}/.test(dobStr)) {
            const y = Number(dobStr.slice(0, 4));
            const m = Number(dobStr.slice(5, 7)) - 1;
            const d = Number(dobStr.slice(8, 10));
            const dobDate = new Date(y, m, d, 12, 0, 0, 0);
            if (!isNaN(dobDate as any)) {
              try {
                setSenderDOB?.(dobDate);
              } catch {}
            }
          } else {
            // Fallback for other formats
            const dobDate = new Date(dobStr);
            if (!isNaN(dobDate as any)) {
              try {
                setSenderDOB?.(dobDate);
              } catch {}
            }
          }
        }
      }
    } catch (e) {
      console.warn("PIN verification failed", e);
      handlePinVerificationFailure();
      return;
    }
    if (pinErrorResetTimeoutRef.current) {
      clearTimeout(pinErrorResetTimeoutRef.current);
      pinErrorResetTimeoutRef.current = null;
    }
    setShowPinAttemptsWarning(false);
    const targetIndex = steps.findIndex((s) => s.key === "country");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
      return;
    }
  };

  useEffect(() => {
    return () => {
      if (pinErrorResetTimeoutRef.current) {
        clearTimeout(pinErrorResetTimeoutRef.current);
      }
    };
  }, []);

  const handleReceiverInformation = () => {
    if (
      returnToStep === "reviewAndEditStep" ||
      returnToStep === "finalReview"
    ) {
      captureReceiverNameConfirmationEvent(
        storeFirst,
        storeMiddle,
        storeLast,
        storeLast2,
      );
      const backIdx = steps.findIndex((s) => s.key === returnToStep);
      setReturnToStep(null);
      setIsReceiverFullnameConfirm(false);
      if (backIdx !== -1) {
        setCurrentStep(backIdx);
      }
      return;
    }
    try {
      const gffpFieldsByCategoryLocal =
        useSessionStore.getState().gffpFieldsByCategory || {};
      const consumerId = consumerIdFromStore
        ? String(consumerIdFromStore)
        : "0";
      const validationReq = buildSendValidationRequestFromStore(consumerId);
      const missing = filterSendFlowMissingGffpFields(
        new RequiredFields().getMissingFields(
          gffpFieldsByCategoryLocal,
          validationReq,
        ),
      );
      if (missing && missing.length > 0) {
        try {
          setMissingRequiredFields?.(missing as any);
        } catch {}
        skippedAdditionalInformationBeforeFraudRef.current = false;
        additionalInformationNavSourceRef.current = "receiverReturnToFraud";
        // Route to Additional Information, then return back to Fraud Prevention after completion
        setReturnToStep("fraudPrevention");
        const aiIndex = steps.findIndex(
          (s) => s.key === "additionalInformation",
        );
        if (aiIndex !== -1) {
          setCurrentStep(aiIndex);
          return;
        }
      }
    } catch (e) {
      console.warn("Failed to compute missing fields after receiverName", e);
    }
    captureReceiverNameConfirmationEvent(
      storeFirst,
      storeMiddle,
      storeLast,
      storeLast2,
    );
    // No missing fields → proceed to Fraud Prevention
    skippedAdditionalInformationBeforeFraudRef.current = true;
    const targetIndex = steps.findIndex((s) => s.key === "fraudPrevention");
    if (targetIndex !== -1) {
      try {
        recordStepTransition?.("fraudPrevention");
      } catch {}
      setCurrentStep(targetIndex);
      return;
    }
  };

  const captureAmountEvent = (
    currentSendAmount: string,
    theyReceiveAmount: any,
  ) => {
    capturePosthogEvent("Send Flow - Amount Capture Step", {
      "data-mg-sending-amount": currentSendAmount,
      "data-mg-receiving-amount": theyReceiveAmount,
    });
  };

  const captureInitialReviewWithPromoCodeEvent = (
    promocodeValue: string,
    isPromocodeApplied: boolean,
  ) => {
    capturePosthogEvent("Send Flow - Initial Review with Promo Code Step", {
      "data-mg-promocode": isPromocodeApplied ? promocodeValue : "N/A",
    });
  };

  const captureSenderFullnameEvent = (
    senderFirstName: string,
    senderMiddleName: string,
    senderLastName: string,
  ) => {
    capturePosthogEvent("Send Flow - Sender Name Capture Step", {
      "data-mg-sender-firstName": senderFirstName,
      "data-mg-sender-middleName": senderMiddleName,
      "data-mg-sender-lastName": senderLastName,
    });
  };

  const captureSenderAddressEvent = (
    senderAddress: string,
    senderCity: string,
    senderZipCode: string,
    senderState: string,
  ) => {
    capturePosthogEvent("Send Flow - Sender Address Capture Step", {
      "data-mg-sender-address": senderAddress,
      "data-mg-sender-city": senderCity,
      "data-mg-sender-zipcode": senderZipCode,
      "data-mg-sender-state": senderState,
    });
  };

  const captureSenderDOBEvent = (senderDOB: string) => {
    capturePosthogEvent("Send Flow - Sender DOB Capture Step", {
      "data-mg-sender-dob": senderDOB,
    });
  };

  const captureSenderDOBConfirmationEvent = (senderDOB: string) => {
    capturePosthogEvent("Send Flow - Sender DOB Confirmation Step", {
      "data-mg-sender-dob": senderDOB,
    });
  };

  const captureSenderIdType = (
    selectedIdTypeKey: string,
    selectedIdTypeValue: string,
    selectedIdTypeLabel: string,
    selectedIdPhotoIdCountry: string,
    selectedIdPhotoIdStateAbbr: string,
    selectedIdPhotoIdStateName: string,
  ) => {
    capturePosthogEvent("Send Flow - Sender ID Type Capture Step", {
      "data-mg-sender-id-type-key": selectedIdTypeKey,
      "data-mg-sender-id-type-value": selectedIdTypeValue,
      "data-mg-sender-id-type-label": selectedIdTypeLabel,
      "data-mg-sender-photoId-country": selectedIdPhotoIdCountry,
      "data-mg-sender-photoId-state-abbr": selectedIdPhotoIdStateAbbr,
      "data-mg-sender-photoId-state-name": selectedIdPhotoIdStateName,
    });
  };
  const captureSenderIdentificationNumbervent = (
    senderPhotoIdNumber: string,
  ) => {
    capturePosthogEvent("Send Flow - Sender ID Number Capture Step", {
      "data-mg-sender-identification-number": senderPhotoIdNumber,
    });
  };

  const captureReceiverNameEvent = (
    storeFirst: string,
    storeMiddle: string,
    storeLast: string,
    storeLast2: string,
  ) => {
    capturePosthogEvent("Send Flow - Receiver Name Capture Step", {
      "data-mg-receiver-firstname": storeFirst,
      "data-mg-receiver-middlename": storeMiddle,
      "data-mg-receiver-lastname": storeLast,
      "data-mg-receiver-lastname2": storeLast2,
    });
  };

  const captureReceiverNameConfirmationEvent = (
    storeFirst: string,
    storeMiddle: string,
    storeLast: string,
    storeLast2: string,
  ) => {
    capturePosthogEvent("Send Flow - Receiver Name Confirmation Step", {
      "data-mg-receiver-firstname": storeFirst,
      "data-mg-receiver-middlename": storeMiddle,
      "data-mg-receiver-lastname": storeLast,
      "data-mg-receiver-lastname2": storeLast2,
    });
  };

  const captureFraudPreventionEvent = () => {
    capturePosthogEvent("Send Flow - Fraud Prevention Step", {
      "data-mg-fraud-prevention": "true",
    });
  };

  const captureFinalReviewEvent = () => {
    capturePosthogEvent("Send Flow - Final Review Step", {
      "data-mg-final-review": "true",
    });
  };

  const captureConsumerPinEvent = (consumerPin: string) => {
    capturePosthogEvent("Send Flow - Consumer PIN Capture Step", {
      "data-mg-consumer-pin": consumerPin,
    });
  };

  const captureConfirmPinEvent = (consumerPin: string) => {
    capturePosthogEvent("Send Flow - Confirm PIN Step", {
      "data-mg-consumer-pin": consumerPin,
    });
  };

  const capturePinCreatedEvent = () => {
    capturePosthogEvent("Send Flow - PIN Created Step", {
      "data-mg-pin-created": "true",
    });
  };

  /**
   * When the user opened a step from Review/Final via Edit (`returnToStep`), Continue should run
   * that step's validation first, then return to the review step — not skip straight back (which
   * bypassed `submit()` on sender name / address / ID / receiver, etc.).
   */
  const navigateAfterEditOrForward = (
    forwardStepKey: string,
    recordWhenForwardTo?: string,
  ) => {
    const backKey = returnToStep;
    if (backKey) {
      const backIdx = steps.findIndex((s) => s.key === backKey);
      setReturnToStep(null);
      if (backIdx !== -1) {
        try {
          recordStepTransition?.(backKey);
        } catch {}
        setCurrentStep(backIdx);
        return;
      }
    }
    const nextIdx = steps.findIndex((s) => s.key === forwardStepKey);
    if (nextIdx !== -1) {
      if (recordWhenForwardTo) {
        try {
          recordStepTransition?.(recordWhenForwardTo);
        } catch {}
      }
      setCurrentStep(nextIdx);
    }
  };

  const handleNext = async (countryCode?: string) => {
    // Only EnterPhoneStep triggers the ConsumerLookup
    await executeWithLoading(async () => {
      // ---- normal forward navigation below ----
      if (steps[currentStep].key === "phone") {
        try {
          const isValid = await phoneStepRef.current?.phoneSubmit();
          if (!isValid) return;
          const result = await phoneStepRef.current?.submit();
          if (result === "notfound" || result === "externalSenderProfile") {
            // Treat as new consumer (or external profile without local PIN — same flow)
            handleCustomerNotFound();
            setSteps(() => {
              return BASE_STEPS_NEW;
            });
            setSendCashStepsMode?.("new");
            // advance on the next render so BASE_STEPS_NEW is fixed
            requestAnimationFrame(() =>
              setCurrentStep(
                BASE_STEPS_NEW.findIndex((s) => s.key === "country"),
              ),
            );
            return;
          } else {
            // Existing consumer
            setSteps(BASE_STEPS_EXISTING);
            setSendCashStepsMode?.("existing");
            setCurrentStep(1); // move to PinInput
          }
          if (returnToStep) {
            const backKey = returnToStep;
            const backIdx = steps.findIndex((s) => s.key === backKey);
            setReturnToStep(null);
            if (backIdx !== -1) {
              setCurrentStep(backIdx);
              return;
            }
          }
          // Success path: go to PIN step
          const targetIndex = steps.findIndex((s) => s.key === "pin");
          if (targetIndex !== -1) {
            setCurrentStep(targetIndex);
            return;
          }
        } catch {
          // submit already surfaced alerts; just stop progression
          return;
        }
      } else if (steps[currentStep].key === "country") {
        // After selecting a country, branch to state or amount entry (same path when editing from review).
        const newCountry = countryCode || selectedDestinationCountryCode;
        const previousCountry = (feeLookupParamsFromStore as any)
          ?.destinationCountry;
        if (
          previousCountry &&
          newCountry &&
          String(previousCountry).toUpperCase() !==
            String(newCountry).toUpperCase()
        ) {
          clearStaleDeliverySelection();
        }
        const isUsOrCa = newCountry === "USA" || newCountry === "CAN";
        const targetKey = isUsOrCa ? "state" : "sendingAmountEntry";
        const targetIndex = steps.findIndex((s) => s.key === targetKey);
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "state") {
        const targetIndex = steps.findIndex(
          (s) => s.key === "sendingAmountEntry",
        );
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "otp") {
        // OTP verification step - after OTP is verified, proceed to enter new PIN
        // TODO: Add actual OTP verification API call here
        // For now, just proceed to PIN step
        const pinIndex = steps.findIndex((s) => s.key === "pin");
        if (pinIndex !== -1) {
          try {
            recordStepTransition?.("pin");
          } catch {}
          setCurrentStep(pinIndex);
          return;
        }
      } else if (steps[currentStep].key === "pin") {
        // Check if we're in reset PIN flow (steps array contains "otp" key)
        const isResetPinFlow = steps.some((s) => s.key === "otp");

        if (isResetPinFlow) {
          // In reset PIN flow: after entering new PIN, go to confirm PIN
          captureConsumerPinEvent(consumerPin);
          const confirmIndex = steps.findIndex((s) => s.key === "confirmPin");
          if (confirmIndex !== -1) {
            try {
              recordStepTransition?.("confirmPin");
            } catch {}
            setCurrentStep(confirmIndex);
            return;
          }
        } else if (isNewConsumer) {
          // If this is a new consumer, go to confirm PIN step instead of auth
          captureConsumerPinEvent(consumerPin);
          const confirmIndex = steps.findIndex((s) => s.key === "confirmPin");
          if (confirmIndex !== -1) {
            try {
              recordStepTransition?.("confirmPin");
            } catch {}
            setCurrentStep(confirmIndex);
            return;
          }
        } else {
          // Existing consumer: build the auth consumer request and proceed to choose country
          handlePinVerification();
        }
      } else if (steps[currentStep].key === "confirmPin") {
        // Check if we're in reset PIN flow
        const isResetPinFlow = steps.some((s) => s.key === "otp");

        if (isResetPinFlow) {
          // In reset PIN flow: after confirming PIN, go to PIN created
          captureConfirmPinEvent(consumerPin);
          const pinCreatedIndex = steps.findIndex(
            (s) => s.key === "pinCreated",
          );
          if (pinCreatedIndex !== -1) {
            try {
              recordStepTransition?.("pinCreated");
            } catch {}
            setCurrentStep(pinCreatedIndex);
            return;
          }
        } else {
          // Normal flow
          captureConfirmPinEvent(consumerPin);
          // After confirming PIN for a new consumer, go to PIN created or head to cashier path
          // No early redirect to additionalInformation here; we will handle missing fields after receiverName
          const result = await maultipleCalls();
          if (result && (result as any).error) {
            setConfirmPinApiError(
              (result as any).errorMessage ?? "An error occurred.",
            );
            return;
          }
          if (isNewConsumer) {
            const targetIndex = steps.findIndex((s) => s.key === "pinCreated");
            if (targetIndex !== -1) {
              try {
                recordStepTransition?.("pinCreated");
              } catch {}
              setCurrentStep(targetIndex);
              return;
            }
          }

          const targetIndex = steps.findIndex((s) => s.key === "headToCashier");
          if (targetIndex !== -1) {
            try {
              recordStepTransition?.("headToCashier");
            } catch {}
            setCurrentStep(targetIndex);
            return;
          }
        }
      } else if (steps[currentStep].key === "sendingAmountEntry") {
        if (!isStepValid) {
          showError(t("ERROR_PLEASE_ENTER_A_VALID_AMOUNT"));
          return;
        }
        // Call FeeLookup with updated amount before moving to HowTheyReceive
        if (
          agentId &&
          unitProfileId &&
          selectedDestinationCountryCode &&
          currentSendAmount
        ) {
          const amountNum = Number(String(currentSendAmount).replace(/,/g, ""));
          if (isFinite(amountNum) && amountNum > 0) {
            const feeLookupParams = {
              agentID: agentId,
              unitProfileID: unitProfileId,
              amount: amountNum,
              amountExcludingFee: amountNum,
              sendCurrency:
                pendingSendOption?.SendAmounts?.SendCurrency || "USD",
              destinationCountry: selectedDestinationCountryCode,
              destinationState: selectedDestinationStateAbbr,
              receiveCurrency: selectedReceiveCurrency || undefined,
              allOptions: true,
              productType: ProductType.SEND,
              promoCodes: [] as string[],
            } as const;

            // Same sources as handleApplyPromoCode — do not call useSessionStore() here (hooks only at top level).
            const trimmedLocal = (promoCode || "").trim();
            const storedCodes = (feeLookupParamsFromStore as any)?.promoCodes;
            const trimmedStore =
              Array.isArray(storedCodes) && storedCodes.length > 0
                ? String(storedCodes[0] ?? "").trim()
                : "";
            const promoForLookup =
              trimmedLocal || (isPromocodeApplied ? trimmedStore : "");
            if (promoForLookup) {
              (feeLookupParams as any).promoCodes = [promoForLookup];
              setPreApplyFees(summary.fees);
              const prePromoRate = FeeLookupHelper.getExchangeRate(
                option as any,
              );
              setPreApplyExchangeRate(
                typeof prePromoRate === "number" && !Number.isNaN(prePromoRate)
                  ? prePromoRate
                  : null,
              );
            } else {
              setPreApplyFees(null);
              setPostApplyFees(null);
              setPreApplyExchangeRate(null);
            }

            try {
              setSendFeeLookupReviewEditStore?.(feeLookupParams as any);
            } catch {}
            const feeResp = await postFeeLookup(feeLookupParams as any);
            let parsed: any;
            // Inspect potential error envelope in fee lookup response
            try {
              const rawData: any =
                feeResp && (feeResp as any).data !== undefined
                  ? (feeResp as any).data
                  : feeResp;
              parsed = rawData;
              if (typeof rawData === "string") {
                try {
                  parsed = JSON.parse(rawData);
                } catch {}
              }
              const errDetail = parsed?.ErrorDetail || parsed?.errorDetail;
              if (errDetail) {
                const errCode =
                  errDetail?.ErrorCode ?? errDetail?.errorCode ?? "UNKNOWN";
                const errString =
                  errDetail?.ErrorString ??
                  errDetail?.errorString ??
                  "An error occurred.";
                console.warn("FeeLookup ErrorDetail:", { errCode, errString });
                // Alert.alert('Message', String(errString), [{ text: 'OK' }]);
                if (promoForLookup) {
                  setPreApplyFees(null);
                  setPostApplyFees(null);
                  setPreApplyExchangeRate(null);
                }
                showError(errCode + " " + errString);
                return; // stay on current step if fee lookup returned an error
              }
            } catch {}
            try {
              setLastFeeLookupResponse?.(parsed);
            } catch {}

            const feeLookupStoreSetters = {
              setSelectedSendOption: setSelectedSendOptionStore,
              setPendingSendOption: setPendingSendOptionStore,
              setTheyReceiveAmount,
              setSelectedReceiveCurrency,
            };

            // Keep promo fee strikethrough / post-apply totals in sync with handleApplyPromoCode()
            if (promoForLookup && parsed != null) {
              const feeInfos: any[] = extractFeeInfos(parsed);
              const current = option || {};
              const match = findMatchingFeeInfo(feeInfos, current);
              if (!match) {
                setPreApplyFees(null);
                setPostApplyFees(null);
                setPreApplyExchangeRate(null);
              } else {
                const promoArray: any[] = Array.isArray(match?.PromotionInfo)
                  ? match?.PromotionInfo
                  : [];
                const hasError = promotionInfoHasError(promoArray);
                const isPoboPromo = promotionInfoIsPobo(promoArray);
                if (!hasError) {
                  if (isPoboPromo) {
                    try {
                      setPostApplyFees(null);
                    } catch {}
                    syncSendOptionFromFeeLookupMatch(match, feeLookupStoreSetters);
                  } else {
                    const discountAmt =
                      Number(match?.SendAmounts?.TotalDiscountAmount ?? 0) ||
                      Number(
                        promoArray?.reduce(
                          (sum: number, p: any) =>
                            sum + Number(p?.PromotionDiscountAmount || 0),
                          0,
                        ),
                      );
                    const originalFees = Number(
                      match?.SendAmounts?.TotalSendFees ?? 0,
                    );
                    const taxes = Number(
                      match?.SendAmounts?.TotalSendTaxes ?? 0,
                    );
                    const sendAmt = Number(match?.SendAmounts?.SendAmount ?? 0);
                    const discountedFees = Math.max(
                      0,
                      originalFees - (discountAmt || 0),
                    );
                    const totalAmountToCollect =
                      typeof match?.SendAmounts?.TotalAmountToCollect ===
                      "number"
                        ? Number(match?.SendAmounts?.TotalAmountToCollect)
                        : sendAmt + discountedFees + taxes;
                    const updatedOption = {
                      ...match,
                      SendAmounts: {
                        ...(match?.SendAmounts || {}),
                        TotalDiscountAmount:
                          discountAmt ||
                          match?.SendAmounts?.TotalDiscountAmount,
                        TotalSendFees: discountedFees,
                        TotalAmountToCollect: totalAmountToCollect,
                      },
                      TotalAmount: totalAmountToCollect,
                    } as any;
                    try {
                      setPreApplyFees(originalFees);
                      setPostApplyFees(
                        Number(match?.SendAmounts?.TotalSendFees),
                      );
                    } catch {}
                    syncSendOptionFromFeeLookupMatch(
                      updatedOption,
                      feeLookupStoreSetters,
                    );
                  }
                } else {
                  setPreApplyFees(null);
                  setPostApplyFees(null);
                  setPreApplyExchangeRate(null);
                }
              }
            } else if (parsed != null) {
              const sessionSnap = useSessionStore.getState() as any;
              const currentOpt =
                sessionSnap.pendingSendOption ||
                sessionSnap.selectedSendOption ||
                option ||
                null;
              if (currentOpt && hasDeliverySelectionIdentity(currentOpt)) {
                const match = findMatchingFeeInfo(
                  extractFeeInfos(parsed),
                  currentOpt,
                );
                if (match) {
                  syncSendOptionFromFeeLookupMatch(match, feeLookupStoreSetters);
                } else {
                  clearStaleDeliverySelection();
                }
              }
            }
          }
        }
        // After amount change, always go through HowTheyReceive before review.
        captureAmountEvent(currentSendAmount, theyReceiveAmount);
        const targetIndex = steps.findIndex((s) => s.key === "howTheyReceive");
        if (targetIndex !== -1) {
          if (!returnToStep) {
            clearStaleDeliverySelection();
          }
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "howTheyReceive") {
        /*else if (currentStep < steps.length - 1) {
        setCurrentStep((prev) => prev + 1);
        return;
      }*/
        // Always read latest pending option from the store. Auto-advance runs in setTimeout(0) while
        // `isStepValid` in this closure can still be stale; the child sets pending synchronously.
        const sessionSnap = useSessionStore.getState() as any;
        const pendingFromStore = sessionSnap.pendingSendOption;
        if (!pendingFromStore) {
          showError(t("ERROR_PLEASE_SELECT_DELIVERY_OPTION"));
          return;
        }
        const editingFromFinalReview = returnToStep === "finalReview";
        const deliverySelectionBefore = getSendDeliverySelectionKey(
          selectedSendOptionValue ?? sessionSnap.selectedSendOption,
        );
        const deliverySelectionAfter =
          getSendDeliverySelectionKey(pendingFromStore);
        const deliverySelectionChanged =
          deliverySelectionAfter !== "" &&
          deliverySelectionBefore !== deliverySelectionAfter;

        const trimmedLocal = (promoCode || "").trim();
        const storedCodes = (feeLookupParamsFromStore as any)?.promoCodes;
        const trimmedStore =
          Array.isArray(storedCodes) && storedCodes.length > 0
            ? String(storedCodes[0] ?? "").trim()
            : "";
        const promoForLookup =
          trimmedLocal || (isPromocodeApplied ? trimmedStore : "");

        // If a promo is active, re-run fee lookup for the newly chosen delivery so gradient / totals
        // and preApplyFees–postApplyFees stay in sync (same rules as handleApplyPromoCode).
        if (promoForLookup) {
          setPreApplyFees(
            Number(pendingFromStore?.SendAmounts?.TotalDiscountAmount || 0) +
              Number(pendingFromStore?.SendAmounts?.TotalSendFees || 0),
          );
          const prePromoRate = FeeLookupHelper.getExchangeRate(
            pendingFromStore as any,
          );
          setPreApplyExchangeRate(
            typeof prePromoRate === "number" && !Number.isNaN(prePromoRate)
              ? prePromoRate
              : null,
          );

          const updatedParams = {
            ...(feeLookupParamsFromStore || {}),
            promoCodes: [promoForLookup],
            deliveryOption:
              (sessionSnap.selectedDeliveryOptionStore as any)
                ?.DeliveryOption ?? undefined,
            allOptions: false,
          } as any;

          try {
            setSendFeeLookupReviewEditStore?.(updatedParams);
          } catch {}

          const feeResp = await postFeeLookup(updatedParams as any);
          const rawData: any =
            feeResp && (feeResp as any).data !== undefined
              ? (feeResp as any).data
              : feeResp;
          let parsed: any = rawData;
          if (typeof rawData === "string") {
            try {
              parsed = JSON.parse(rawData);
            } catch {}
          }

          const errDetail = parsed?.ErrorDetail || parsed?.errorDetail;
          if (errDetail) {
            const errString =
              errDetail?.ErrorString ??
              errDetail?.errorString ??
              "An error occurred.";
            setPreApplyFees(null);
            setPostApplyFees(null);
            setPreApplyExchangeRate(null);
            showError(String(errString));
            return;
          }

          try {
            setLastFeeLookupResponse?.(parsed);
          } catch {}

          const feeInfos: any[] = Array.isArray(parsed?.FeeInfos)
            ? parsed?.FeeInfos
            : Array.isArray(parsed?.FeeInfo)
              ? parsed?.FeeInfo
              : [];

          const match = findMatchingFeeInfo(feeInfos, pendingFromStore);
          if (!match) {
            setPreApplyFees(null);
            setPostApplyFees(null);
            setPreApplyExchangeRate(null);
            showError(t("ERROR_INVALID_PROMOCODE"));
            return;
          }

          const promoArray: any[] = Array.isArray(match?.PromotionInfo)
            ? match?.PromotionInfo
            : [];
          const hasError = promotionInfoHasError(promoArray);
          const isPoboPromo = promotionInfoIsPobo(promoArray);

          if (hasError) {
            setPreApplyFees(null);
            setPostApplyFees(null);
            setPreApplyExchangeRate(null);
            showError(t("ERROR_INVALID_PROMOCODE"));
            return;
          }

          if (isPoboPromo) {
            const updatedOption = { ...match } as any;
            try {
              setPostApplyFees(null);
            } catch {}
            try {
              setSelectedSendOptionStore?.(updatedOption);
            } catch {}
            try {
              setPendingSendOptionStore?.(updatedOption);
            } catch {}
          } else {
            setPreApplyExchangeRate(null);
            const discountAmt =
              Number(match?.SendAmounts?.TotalDiscountAmount ?? 0) ||
              Number(
                promoArray?.reduce(
                  (sum: number, p: any) =>
                    sum + Number(p?.PromotionDiscountAmount || 0),
                  0,
                ),
              );
            const originalFees = Number(match?.SendAmounts?.TotalSendFees ?? 0);
            const taxes = Number(match?.SendAmounts?.TotalSendTaxes ?? 0);
            const sendAmt = Number(match?.SendAmounts?.SendAmount ?? 0);
            const discountedFees = Math.max(
              0,
              originalFees - (discountAmt || 0),
            );
            const totalAmountToCollect =
              typeof match?.SendAmounts?.TotalAmountToCollect === "number"
                ? Number(match?.SendAmounts?.TotalAmountToCollect)
                : sendAmt + discountedFees + taxes;

            const updatedOption = {
              ...match,
              SendAmounts: {
                ...(match?.SendAmounts || {}),
                TotalDiscountAmount:
                  discountAmt || match?.SendAmounts?.TotalDiscountAmount,
                TotalSendFees: discountedFees,
                TotalAmountToCollect: totalAmountToCollect,
              },
              TotalAmount: totalAmountToCollect,
            } as any;

            try {
              setPostApplyFees(Number(match?.SendAmounts?.TotalSendFees));
            } catch {}
            try {
              setSelectedSendOptionStore?.(updatedOption);
            } catch {}
            try {
              setPendingSendOptionStore?.(updatedOption);
            } catch {}
          }
        } else {
          const latestResp = (useSessionStore.getState() as any)
            .lastFeeLookupResponse;
          const match = findMatchingFeeInfo(
            extractFeeInfos(latestResp),
            pendingFromStore,
          );
          if (!match) {
            showError(t("ERROR_PLEASE_SELECT_DELIVERY_OPTION"));
            return;
          }
          const toCommit = syncSendOptionFromFeeLookupMatch(match, {
            setSelectedSendOption: setSelectedSendOption,
            setPendingSendOption: setPendingSendOptionStore,
            setTheyReceiveAmount,
            setSelectedReceiveCurrency,
          });
          setSelectedSendOption(toCommit);
        }

        if (editingFromFinalReview && deliverySelectionChanged) {
          const syncResult = await syncSendAdditionalFieldsAfterDeliveryChange({
            invalidateCachedReqFields: true,
          });

          if (syncResult !== null) {
            if ((syncResult?.length ?? 0) > 0) {
              skippedAdditionalInformationBeforeFraudRef.current = false;
              additionalInformationNavSourceRef.current = "finalReview";
              setReturnToStep("finalReview");
              const aiIndex = steps.findIndex(
                (s) => s.key === "additionalInformation",
              );
              if (aiIndex !== -1) {
                try {
                  recordStepTransition?.("additionalInformation");
                } catch {}
                setCurrentStep(aiIndex);
                return;
              }
            }

            const finalReviewIndex = steps.findIndex(
              (s) => s.key === "finalReview",
            );
            if (finalReviewIndex !== -1) {
              setReturnToStep(null);
              try {
                recordStepTransition?.("finalReview");
              } catch {}
              setCurrentStep(finalReviewIndex);
              return;
            }
          }
        }

        const targetKey = returnToStep || "reviewAndEditStep";
        const targetIndex = steps.findIndex((s) => s.key === targetKey);
        if (targetIndex !== -1) {
          setReturnToStep(null);
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "reviewAndEditStep") {
        setReviewStepApiError(null);
        // Call GetFieldsForProduct on Continue from Review step
        try {
          const opt: any = pendingSendOption || selectedSendOptionValue || {};
          const amountFromState = Number(
            String(currentSendAmount || "0").replace(/,/g, ""),
          );
          const amountFromOption = Number(opt?.SendAmounts?.SendAmount ?? 0);
          const payload: any = {
            unitProfileID: unitProfileId || "",
            agentID: agentId || "",
            receiveCountry:
              selectedDestinationCountryCode || opt?.DestinationCountry || "",
            deliveryOption: opt?.DeliveryOption || "",
            thirdPartyType: "NONE",
            receiveCurrency:
              selectedReceiveCurrency ||
              opt?.ValidReceiveCurrency ||
              opt?.EstimatedReceiveCurrency ||
              "",
            amount:
              isFinite(amountFromOption) && amountFromOption > 0
                ? amountFromOption
                : isFinite(amountFromState)
                  ? amountFromState
                  : 0,
            sendCurrency: opt?.SendAmounts?.SendCurrency || "USD",
            productType: ProductType.SEND,
            CustomerId: "0",
            receiveAgentID:
              opt?.ReceiveAgentId ??
              (opt as any)?.receiveAgentID ??
              opt?.ReceiveAgentID ??
              undefined,
          };
          const gffpResponse: FieldsForProductInfo =
            await getFieldsForProductRequest(payload);
          const requiredFields: any[] =
            GffpHelper.filterRequiredAndOptionalFields(gffpResponse) as any[];

          // Group by FieldCategory with full field objects
          const groupedByCategory: Record<string, any[]> =
            requiredFields.reduce((acc: Record<string, any[]>, field: any) => {
              const category =
                field.FieldCategory ?? field.fieldCategory ?? "Uncategorized";
              if (!acc[category]) acc[category] = [];
              acc[category].push(field);
              return acc;
            }, {});

          // Stable sort within each category by DisplayOrder (if present)
          Object.keys(groupedByCategory).forEach((cat) => {
            groupedByCategory[cat].sort((a, b) => {
              const ao = a.DisplayOrder ?? a.displayOrder ?? 0;
              const bo = b.DisplayOrder ?? b.displayOrder ?? 0;
              return ao - bo;
            });
          });

          const distinctCategories = Object.keys(groupedByCategory);

          try {
            setGffpFieldsByCategory?.(groupedByCategory);
          } catch {}
          try {
            setGffpCategories?.(distinctCategories);
          } catch {}
        } catch (err: any) {
          const errMsg =
            err?.message || t("ERROR_UNABLE_TO_LOAD_REQUIRED_FIELDS");
          setReviewStepApiError(String(errMsg));
          try {
            showError(String(errMsg));
          } catch {}
          return; // stay on review step on failure
        }

        captureInitialReviewWithPromoCodeEvent(promoCode, isPromocodeApplied);
        // setIsGradientExpand(false)
        // Success → proceed to next step
        const targetIndex = steps.findIndex((s) => s.key === "senderName");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderName") {
        const isValid = await SenderFullNameStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        captureSenderFullnameEvent(
          senderFirstName,
          senderMiddleName,
          senderLastName,
        );
        navigateAfterEditOrForward("senderAddress");
        return;
      } else if (steps[currentStep].key === "senderAddress") {
        const isValid = await SenderAddressStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        captureSenderAddressEvent(
          senderAddress,
          senderCity,
          senderZipCode,
          senderState,
        );
        navigateAfterEditOrForward("senderDOB");
        return;
      } else if (steps[currentStep].key === "senderDOB") {
        const isValid = await SenderDOBStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        captureSenderDOBEvent(storedDobString);
        setIsDOBCOnfirm(true);

        // const targetIndex = steps.findIndex(s => s.key === "senderIdentification");
        // if (targetIndex !== -1) {
        //   setCurrentStep(targetIndex);
        //   return;
        // }
      } else if (steps[currentStep].key === "senderIdentification") {
        // Fresh read from Zustand so we always use the ID type just written by SenderIdentificationTypeStep.
        const session = useSessionStore.getState() as any;
        const selectedTypeKey = session.selectedIdTypeKey as
          | string
          | null
          | undefined;
        const idPhotoStateAbbr = session.selectedIdPhotoIdStateAbbr as
          | string
          | null
          | undefined;

        captureSenderIdType(
          selectedTypeKey ?? "",
          session.selectedIdTypeValue ?? "",
          session.selectedIdTypeLabel ?? "",
          session.selectedIdPhotoIdCountry ?? "",
          idPhotoStateAbbr ?? "",
          session.selectedIdPhotoIdStateName ?? "",
        );

        // Mexican Electoral and Passport skip Choose State of ID (all other types go through stateOfId).
        const skipChooseStateOfId =
          selectedTypeKey === MEXICAN_ELECTORAL_ID_TYPE_KEY ||
          selectedTypeKey === PASSPORT_ID_TYPE_KEY;
        const nextKey = skipChooseStateOfId ? "senderIdNumber" : "stateOfId";
        const targetIndex = steps.findIndex((s) => s.key === nextKey);
        if (targetIndex !== -1) {
          try {
            recordStepTransition?.(nextKey);
          } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "stateOfId") {
        // captureSenderIdType()
        const targetIndex = steps.findIndex((s) => s.key === "senderIdNumber");
        if (targetIndex !== -1) {
          try {
            recordStepTransition?.("senderIdNumber");
          } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderIdNumber") {
        const isValid = await SenderIdentificationStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        captureSenderIdentificationNumbervent(senderPhotoIdNumber);
        navigateAfterEditOrForward("receiverName", "receiverName");
        return;
      } else if (steps[currentStep].key === "receiverName") {
        const isValid = await ReceiverFullNameStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        // After receiver name, check for any missing required fields from GFFP
        captureReceiverNameEvent(
          storeFirst,
          storeMiddle,
          storeLast,
          storeLast2,
        );
        setIsReceiverFullnameConfirm(true);
      } else if (steps[currentStep].key === "fraudPrevention") {
        // Build and log update consumer profile draft before proceeding
        captureFraudPreventionEvent();
        const targetIndex = steps.findIndex((s) => s.key === "finalReview");
        if (targetIndex !== -1) {
          try {
            recordStepTransition?.("finalReview");
          } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "finalReview") {
        captureFinalReviewEvent();
        // If this is a new consumer, send them to the PIN step before proceeding
        if (isNewConsumer) {
          const pinIndex = steps.findIndex((s) => s.key === "pin");
          if (pinIndex !== -1) {
            try {
              recordStepTransition?.("pin");
            } catch {}
            setCurrentStep(pinIndex);
            return;
          }
        } else {
          // For existing consumers, perform update/validate/post flow here.
          // If validation reports missing fields, redirect to Additional Information.
          try {
            const result = await maultipleCalls();
            if (result && (result as any).error) {
              setFinalReviewApiError(
                (result as any).errorMessage ?? t("ERROR_AN_ERROR_OCCURED"),
              );
              return;
            }
            if (result && (result as any).missingFieldsFound) {
              skippedAdditionalInformationBeforeFraudRef.current = false;
              additionalInformationNavSourceRef.current = "finalReview";
              const aiIndex = steps.findIndex(
                (s) => s.key === "additionalInformation",
              );
              if (aiIndex !== -1) {
                setCurrentStep(aiIndex);
                return;
              }
            }
          } catch (e) {
            setFinalReviewApiError(
              String((e as any)?.message ?? t("ERROR_AN_ERROR_OCCURED")),
            );
            return;
          }
        }

        const targetIndex = steps.findIndex((s) => s.key === "headToCashier");
        if (targetIndex !== -1) {
          try {
            recordStepTransition?.("headToCashier");
          } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "additionalInformation") {
        const isValid = AdditionalInformationStepRef.current?.submit() ?? false;
        if (!isValid) {
          setIsStepValid(false);
          return;
        }
        // After entering additional info
        if (returnToStep) {
          // Path when we intentionally inserted Additional Information mid-flow (e.g., after receiverName)
          const targetIndex = steps.findIndex((s) => s.key === returnToStep);
          if (targetIndex !== -1) {
            try {
              recordStepTransition?.(returnToStep);
            } catch {}
            additionalInformationNavSourceRef.current = null;
            setCurrentStep(targetIndex);
            setReturnToStep(null);
            return;
          }
        } else {
          // Path when we arrived here from finalReview due to validation requiring more fields
          try {
            const result = await maultipleCalls();
            if (result && (result as any).error) {
              // An error was shown; stay on current step
              return;
            }
            if (result && (result as any).missingFieldsFound) {
              // still missing (should be rare if validations pass)
              return;
            }
          } catch (e) {
            return;
          }
          const targetIndex = steps.findIndex((s) => s.key === "headToCashier");
          if (targetIndex !== -1) {
            try {
              recordStepTransition?.("headToCashier");
            } catch {}
            additionalInformationNavSourceRef.current = null;
            setCurrentStep(targetIndex);
            return;
          }
        }
      } else if (steps[currentStep].key === "pinCreated") {
        capturePinCreatedEvent();
        // Check if we're in reset PIN flow
        const isResetPinFlow = steps.some((s) => s.key === "otp");

        if (isResetPinFlow) {
          // After PIN is created in reset flow, return to main existing consumer flow
          // Reset to BASE_STEPS_EXISTING and navigate to country step
          setSteps(BASE_STEPS_EXISTING);
          setSendCashStepsMode?.("existing");
          const countryIndex = BASE_STEPS_EXISTING.findIndex(
            (s) => s.key === "country",
          );
          if (countryIndex !== -1) {
            try {
              recordStepTransition?.("country");
            } catch {}
            setCurrentStep(countryIndex);
            return;
          }
        } else {
          // Normal flow: go to headToCashier
          const targetIndex = steps.findIndex((s) => s.key === "headToCashier");
          if (targetIndex !== -1) {
            try {
              recordStepTransition?.("headToCashier");
            } catch {}
            setCurrentStep(targetIndex);
            return;
          }
        }
      } else if (steps[currentStep].key === "headToCashier") {
        // Finish: allow host app to handle navigation back to dashboard with kiosk token
        try {
          handleFinish?.();
        } catch {}
        const targetIndex = steps.findIndex((s) => s.key === "headToCashier");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      }
    }, t("STRKEY_PROCESSING"));
  };

  const handleSkip = () => {
    // Run validation + transaction only; navigate to HeadToCashier only on successful validateConsumer
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    executeWithLoading(async () => {
      try {
        capturePosthogEvent('Send Flow - Continue as Guest', {
          "data-mg-skip-flow": "true",
          "data-mg-phone-number": useSessionStore.getState().phoneNumber,
        });

        const result = await maultipleCalls({ skipUpdateProfile: true });
        if (!result || !(result as any).success) {
          return;
        }

        const targetIndex = steps.findIndex((s) => s.key === "headToCashier");
        if (targetIndex !== -1) {
          try {
            recordStepTransition?.("headToCashier");
          } catch {}
          setCurrentStep(targetIndex);
        }
      } catch (e) {
        console.warn("Skip flow failed", e);
        showError(
          String((e as any)?.message ?? t("ERROR_VALIDATION_FAILED")),
        );
      }
    }, t("STRKEY_PROCESSING"));
  };

  const maultipleCalls = async (options?: { skipUpdateProfile?: boolean }) => {
    const skipUpdate = !!options?.skipUpdateProfile;
    try {
      let effectiveConsumerId: string | undefined = undefined;
      if (!skipUpdate) {
        const profile: SendMoneyPostUpdateConsumerProfileParameter =
          logUpdateConsumerProfileDraft();
        profile.unitProfileID = unitProfileId || "";
        profile.agentID = agentId || "";
        const profileResponse = await updateConsumerProfile(profile);
        try {
          effectiveConsumerId =
            (profileResponse &&
              (profileResponse.consumerId ||
                profileResponse.ConsumerId ||
                profileResponse.id ||
                profileResponse.Id)) ??
            undefined;
        } catch {}
      } else {
        // For skip flow, attempt to use existing consumerId from store; fall back to "0"
        try {
          effectiveConsumerId = consumerIdFromStore
            ? String(consumerIdFromStore)
            : "0";
        } catch {
          effectiveConsumerId = "0";
        }
      }

      try {
        const validationReq = buildSendValidationRequestFromStore(
          String(effectiveConsumerId ?? "0"),
        );
        // check what are the Req, OPT fields are missing in the validationReq from requiredFields
        // get the gffpFieldsByCategory from the store
        const gffpFieldsByCategory =
          useSessionStore.getState().gffpFieldsByCategory || {};
        const missingRequiredFields = filterSendFlowMissingGffpFields(
          new RequiredFields().getMissingFields(
            gffpFieldsByCategory,
            validationReq,
          ),
        );
        if (
          !skipUpdate &&
          missingRequiredFields &&
          missingRequiredFields.length > 0
        ) {
          try {
            setMissingRequiredFields?.(missingRequiredFields as any);
          } catch {}
          return { missingFieldsFound: true } as const;
        }
        const validationResponse = await validateConsumer(validationReq);
        // Inspect potential error envelope in validation response
        try {
          const errDetail =
            extractErrorDetailFromValidateConsumerResponse(validationResponse);
          if (errDetail) {
            const productFields = [
              ...flattenGffpFieldsByCategory(gffpFieldsByCategory),
              ...((useSessionStore.getState().missingRequiredFields as any[]) ||
                []),
            ];
            const msg = formatValidateConsumerErrorMessage(
              errDetail,
              productFields,
            );
            try {
              showError?.(msg);
            } catch {}
            return { error: true, errorMessage: msg } as const;
          }
        } catch {}
        const ref =
          (validationResponse &&
            (validationResponse.confirmationNumber ||
              validationResponse.ConfirmationNumber ||
              validationResponse.referenceNumber ||
              validationResponse.ReferenceNumber)) ??
          undefined;
        if (!ref) {
          const msg = t("ERROR_VALIDATION_FAILED");
          try {
            showError?.(msg);
          } catch {}
          return { error: true, errorMessage: msg } as const;
        }
        try {
          setConfirmationNumber?.(String(ref));
        } catch {}
        try {
          setFinalSendValidationResponseStore?.(validationResponse);
        } catch {}
        return { success: true } as const;
      } catch (e) {
        const msg = String(
          (e as any)?.message || t("ERROR_VALIDATION_FAILED"),
        );
        try {
          showError?.(msg);
        } catch {}
        return { error: true, errorMessage: msg } as const;
      }
    } catch (e) {
      const msg = String(
        (e as any)?.message || t("ERROR_SOMETHING_WENT_WRONG"),
      );
      try {
        showError?.(msg);
      } catch {}
      return { error: true, errorMessage: msg } as const;
    }
  };

  // Enable Continue button; steps that manage their own validation (sendingAmountEntry, howTheyReceive) must not be defaulted to true
  useEffect(() => {
    const key = steps[currentStep]?.key;
    if (key === "phone") {
      setIsStepValid(false);
    } else if (
      key === "sendingAmountEntry" ||
      key === "howTheyReceive" ||
      key === "additionalInformation"
    ) {
      // Let the step control validity via onValidationChange; start false so Continue is blocked until valid
      setIsStepValid(false);
    } else {
      setIsStepValid(true);
    }
    if (key === "reviewAndEditStep") {
      setReviewStepApiError(null);
    }
    if (key === "finalReview") {
      setFinalReviewApiError(null);
    }
    if (key === "pin" || key === "confirmPin") {
      setPinStepApiError(null);
      setConfirmPinApiError(null);
    }
  }, [currentStep]);

  const handleBack = () => {
    const curKey = steps[currentStep]?.key;
    if (curKey === "phone") {
      handleClose?.();
      return;
    }
    if (currentStep <= 0) return;
    const prevIdx = currentStep - 1;
    const session = useSessionStore.getState() as any;
    const selectedTypeKey = session.selectedIdTypeKey as
      | string
      | null
      | undefined;
    if (
      curKey === "senderIdNumber" &&
      steps[prevIdx]?.key === "stateOfId" &&
      (selectedTypeKey === MEXICAN_ELECTORAL_ID_TYPE_KEY ||
        selectedTypeKey === PASSPORT_ID_TYPE_KEY)
    ) {
      const idIdx = steps.findIndex((s) => s.key === "senderIdentification");
      if (idIdx !== -1) {
        setCurrentStep(idIdx);
        return;
      }
    }
    const destCountry = selectedDestinationCountryCode;
    const isUsOrCaDest = destCountry === "USA" || destCountry === "CAN";
    if (
      curKey === "sendingAmountEntry" &&
      steps[prevIdx]?.key === "state" &&
      !isUsOrCaDest
    ) {
      const countryIdx = steps.findIndex((s) => s.key === "country");
      if (countryIdx !== -1) {
        try {
          recordStepTransition?.("country");
        } catch {}
        setCurrentStep(countryIdx);
        return;
      }
    }
    if (
      curKey === "fraudPrevention" &&
      steps[prevIdx]?.key === "additionalInformation" &&
      skippedAdditionalInformationBeforeFraudRef.current
    ) {
      const receiverIdx = steps.findIndex((s) => s.key === "receiverName");
      if (receiverIdx !== -1) {
        try {
          recordStepTransition?.("receiverName");
        } catch {}
        setCurrentStep(receiverIdx);
        return;
      }
    }
    if (curKey === "additionalInformation") {
      const src = additionalInformationNavSourceRef.current;
      if (src === "finalReview") {
        const frIdx = steps.findIndex((s) => s.key === "finalReview");
        if (frIdx !== -1) {
          try {
            recordStepTransition?.("finalReview");
          } catch {}
          additionalInformationNavSourceRef.current = null;
          setCurrentStep(frIdx);
          return;
        }
      }
      if (src === "receiverReturnToFraud") {
        const rnIdx = steps.findIndex((s) => s.key === "receiverName");
        if (rnIdx !== -1) {
          try {
            recordStepTransition?.("receiverName");
          } catch {}
          setReturnToStep(null);
          additionalInformationNavSourceRef.current = null;
          setCurrentStep(rnIdx);
          return;
        }
      }
    }
    setCurrentStep((prev) => prev - 1);
  };

  const handleCountryEdit = () => {
    const targetIndex = steps.findIndex((s) => s.key === "country");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
    }
  };

  const handleCustomerNotFound = () => {
    const targetIndex = steps.findIndex((s) => s.key === "country");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
    }
  };

  const handleForgotPassword = () => {
    // mark as new consumer so finalReview routes to PIN
    setIsNewConsumer?.(true);
    // Close pop up
    setShowPopup(false);
    setLocked(false);

    //find index from the NEW flow, not the current one
    const newFlow = BASE_STEPS_NEW;
    const countryIndex = newFlow.findIndex((s) => s.key === "country");

    // update both pieces of state together
    setSteps(newFlow);
    setSendCashStepsMode?.("new");
    if (countryIndex !== -1) {
      setCurrentStep(countryIndex);
    }
  };

  const handleResetPin = () => {
    // Close the account locked modal
    setShowPopup(false);
    setLocked(false);

    // Set steps to reset PIN flow: OTP → Enter New PIN → Confirm PIN → PIN Created
    setSteps(RESET_PIN_STEPS);
    setSendCashStepsMode?.("resetPin");

    // Navigate to OTP step (first step in reset PIN flow)
    const otpIndex = RESET_PIN_STEPS.findIndex((s) => s.key === "otp");
    if (otpIndex !== -1) {
      setCurrentStep(otpIndex);
    }
  };

  const closeNeedHelp = () => {
    setNeedHelp(false);
  };

  const handleNeedHelpCall = () => {
    // Calling to provided number
  };

  const currentKey = steps[currentStep]?.key;
  const isBaseNew = steps === BASE_STEPS_NEW; // still only true if literally the same array
  const hideContinueForPin = currentKey === "pin" && isBaseNew;

  const formatDateForPOPup = () => {
    try {
      // Always read latest from store to avoid stale closures
      const { senderDOB: dobFromState, senderDOBString } =
        useSessionStore.getState() as any;

      // Prefer string format to avoid timezone issues from Date objects
      // This ensures consistent display regardless of how the date was originally parsed
      const rawString =
        (typeof dobFromState === "string" ? dobFromState : undefined) ||
        (senderDOBString as string | undefined) ||
        (senderDOBStringFromStore as string | undefined);

      let date: Date | undefined = undefined;

      if (rawString) {
        const s = String(rawString).trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
          // YYYY-MM-DD or ISO; create at noon to avoid TZ shifts
          const y = Number(s.slice(0, 4));
          const m = Number(s.slice(5, 7)) - 1;
          const d = Number(s.slice(8, 10));
          date = new Date(y, m, d, 12, 0, 0, 0);
        } else if (/^(\d{2})\/(\d{2})\/(\d{4})$/.test(s)) {
          // Handle both MM/DD/YYYY and DD/MM/YYYY
          const [, p1, p2, yyyy] = s.match(
            /^(\d{2})\/(\d{2})\/(\d{4})$/,
          ) as RegExpMatchArray;
          const n1 = Number(p1);
          const n2 = Number(p2);
          let mm = n1;
          let dd = n2;
          // If first part > 12, it's DD/MM/YYYY; swap
          if (n1 > 12 && n2 <= 12) {
            mm = n2;
            dd = n1;
          }
          // Create date at noon local time to avoid timezone boundary issues
          date = new Date(Number(yyyy), mm - 1, dd, 12, 0, 0, 0);
        } else {
          const tmp = new Date(s);
          if (!isNaN(tmp as any)) date = tmp;
        }
      }

      // Fallback to Date object from store if string is not available
      if (!date) {
        const asDate: any =
          dobFromState instanceof Date ? dobFromState : undefined;
        date = asDate && !isNaN(asDate as any) ? (asDate as Date) : undefined;
      }

      if (!date || isNaN(date as any)) return "";
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  const handleDOBNavigation = () => {
    captureSenderDOBConfirmationEvent(storedDobString);
    if (
      returnToStep === "reviewAndEditStep" ||
      returnToStep === "finalReview"
    ) {
      const backIdx = steps.findIndex((s) => s.key === returnToStep);
      setReturnToStep(null);
      if (backIdx !== -1) {
        setCurrentStep(backIdx);
      }
      return;
    }
    const targetIndex = steps.findIndex(
      (s) => s.key === "senderIdentification",
    );
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
      return;
    }
  };

  // Calculate dynamic fee data for gradient footer using same logic as ReviewAndEditStep and ReviewFinalStep
  const getGradientFeeData = () => {
    // For finalReview step, use the same logic as ReviewFinalStep
    if (steps[currentStep].key === "finalReview") {
      const getValuesFromLatestFeeLookupResponse =
        lastFeeLookupResponse?.FeeInfos?.find((f: any) => {
          let sameDisplay = false;
          if (selectedDeliveryOptionStore?.DeliveryOption === "WILL_CALL") {
            sameDisplay =
              f?.DeliveryOptDisplayName ===
              selectedDeliveryOptionStore?.option?.DeliveryOptDisplayName;
          } else {
            sameDisplay =
              f?.ReceiveAgentName ===
              selectedDeliveryOptionStore?.option?.ReceiveAgentName;
          }
          const sameAgent =
            (f?.ReceiveAgentId || f?.ReceiveAgentID) ===
            (selectedDeliveryOptionStore?.option?.ReceiveAgentId ||
              selectedDeliveryOptionStore?.option?.ReceiveAgentID);
          return sameDisplay || sameAgent;
        });

      if (!getValuesFromLatestFeeLookupResponse) {
        return {
          exchangeRate: 0,
          fees: 0,
          total: 0,
          sendCurrency: "USD",
          receiveCurrency: "—",
          totalSendTax: 0,
        };
      }

      const sendCurrency: string =
        getValuesFromLatestFeeLookupResponse?.SendAmounts?.SendCurrency ||
        "USD";
      const receiveCurrency: string =
        selectedReceiveCurrency ||
        (getValuesFromLatestFeeLookupResponse
          ? FeeLookupHelper.getReceiveCurrency(
              getValuesFromLatestFeeLookupResponse,
            )
          : "");
      const exchangeRateNum: number = getValuesFromLatestFeeLookupResponse
        ? FeeLookupHelper.getExchangeRate(getValuesFromLatestFeeLookupResponse)
        : 0;
      const fees: number = Number(
        getValuesFromLatestFeeLookupResponse?.SendAmounts?.TotalSendFees || 0,
      );
      const total: number = Number(
        getValuesFromLatestFeeLookupResponse?.SendAmounts?.TotalAmountToCollect,
      );

      const totalSendTax: number = Number(
        getValuesFromLatestFeeLookupResponse?.SendAmounts?.TotalSendTaxes || 0,
      );

      return {
        exchangeRate: exchangeRateNum,
        fees,
        total,
        sendCurrency,
        receiveCurrency,
        totalSendTax,
      };
    }

    // For reviewAndEditStep, use the existing logic
    const option: any = pendingSendOption || selectedSendOption || null;

    if (!option) {
      return {
        exchangeRate: 0,
        fees: 0,
        total: 0,
        sendCurrency: "USD",
        receiveCurrency: "—",
        totalSendTax: 0,
      };
    }

    const sendCurrency: string = option?.SendAmounts?.SendCurrency || "USD";
    const sendAmount: number = Number(option?.SendAmounts?.SendAmount || 0);
    const receiveCurrency: string = option?.ReceiveAmounts
      ?.ValidCurrencyIndicator
      ? option?.ValidReceiveCurrency || ""
      : option?.EstimatedReceiveCurrency || option?.ValidReceiveCurrency || "";
    const receiveAmount: number = Number(
      option?.ValidReceiveAmount ?? option?.EstimatedReceiveAmount ?? 0,
    );
    const exchangeRateNum: number = Number(
      FeeLookupHelper.getExchangeRate(option) || 0,
    );
    const fees: number = Number(option?.SendAmounts?.TotalSendFees || 0);
    const total: number = Number(option?.SendAmounts?.TotalAmountToCollect);

    // const total: number = resolveTotalAmountToCollect(option);
    const totalSendTax: number = Number(
      option?.SendAmounts?.TotalSendTaxes || 0,
    );

    return {
      exchangeRate: exchangeRateNum,
      fees,
      total,
      sendCurrency,
      receiveCurrency,
      totalSendTax,
    };
  };

  const renderDOBPopUp = () => (
    <View style={styles.root}>
      <TouchableOpacity
        style={styles.scrim}
        activeOpacity={1}
        onPress={() => setIsDOBCOnfirm(false)}
      />
      <View style={styles.bottomSheet}>
        <TouchableOpacity
          style={styles.modalHeaderRow}
          onPress={() => {
            setIsDOBCOnfirm(false);
          }}
          testID="senderDOBClose"
        >
          <CloseIcon width={24} height={24} />
        </TouchableOpacity>
        <View style={{ alignItems: "center" }}>
          <CalenderContainer width={64} height={64} />
          <Text style={styles.modalTitle}>
            {t("SENDER_DOB_CONFIRMATION_TITLE")}
          </Text>
          <Text style={styles.modalInfo}>
            <Trans
              i18nKey="SENDER_DOB_CONFIRM_SUBTITLE"
              components={{
                legalId: (
                  <Text
                    style={[styles.modalInfo, { fontWeight: "700" }]}
                  />
                ),
              }}
            />
          </Text>
          <Text style={styles.modalDOB}>{formatDateForPOPup()}</Text>
        </View>
        <View style={styles.footer}>
          <TouchableOpacity
            testID="senderDOBConfirm"
            style={styles.confirmBtn}
            onPress={() => {
              setIsDOBCOnfirm(false);
              handleDOBNavigation();
            }}
          >
            <Text style={[styles.cancelText, { color: Colors.white }]}>
              {t("CONFIRM")}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="senderDOBEdit"
            style={styles.cancelBtn}
            onPress={() => setIsDOBCOnfirm(false)}
          >
            <Text style={[styles.cancelText, { lineHeight: 24 }]}>
              {t("EDIT")}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderReceiverInfoConfirmationPopUp = () => (
    <View style={styles.root}>
      <TouchableOpacity
        style={styles.scrim}
        activeOpacity={1}
        onPress={() => setIsReceiverFullnameConfirm(false)}
      />
      <View style={styles.bottomSheet}>
        <TouchableOpacity
          style={styles.modalHeaderRow}
          onPress={() => {
            setIsReceiverFullnameConfirm(false);
          }}
          testID="senderDOBClose"
        >
          <CloseIcon width={24} height={24} />
        </TouchableOpacity>
        <View style={{ alignItems: "center" }}>
          <GroupId width={64} height={64} />
          <Text style={styles.modalTitle}>{t("IS_THIS_THEIR_LEGAL_NAME")}</Text>
          <Text style={styles.modalInfo}>
            {t("MAKE_SURE_THEIR_NAME_MATCHES")}
          </Text>
          <View style={styles.receiverLegalNameContainer}>
            <Text style={styles.receiverLegalNameText}>
              {receiverFirstName} {receiverLastName}
            </Text>
          </View>
        </View>
        <View style={styles.footer}>
          <TouchableOpacity
            testID="receiverFullNameConfirm"
            style={styles.confirmBtn}
            onPress={() => {
              setIsReceiverFullnameConfirm(false);
              handleReceiverInformation();
            }}
          >
            <Text style={[styles.cancelText, { color: Colors.white }]}>
              {t("CONFIRM")}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            testID="receiverFullNameEdit"
            style={styles.cancelBtn}
            onPress={() => setIsReceiverFullnameConfirm(false)}
          >
            <Text style={[styles.cancelText, { lineHeight: 24 }]}>
              {t("EDIT")}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const effectiveHeaderHeight =
    steps[currentStep].key === "pinCreated" ? 0 : headerHeight;
  const sendStepsWithoutFooter = useMemo(
    () =>
      new Set<string>([
        "country",
        "state",
        "stateOfId",
        "howTheyReceive",
        "senderIdentification",
        "reviewAndEditStep",
        "finalReview",
        // Multi-field steps render Continue inside their own ScrollView (see ReceiverFullNameStep).
        "receiverName",
        "senderName",
        "senderAddress",
        "sendingAmountEntry",
      ]),
    [],
  );
  const hasFooterForCurrentSendStep = !sendStepsWithoutFooter.has(
    steps[currentStep]?.key ?? "",
  );
  const scrollAreaHeight =
    effectiveLayoutHeight > 0
      ? effectiveLayoutHeight -
        effectiveHeaderHeight -
        (hasFooterForCurrentSendStep && footerHeight > 0 ? footerHeight : 0)
      : undefined;
  // Country/state steps have no footer; list can use full height below header
  const countryStateAreaHeight =
    effectiveLayoutHeight > 0 && effectiveHeaderHeight >= 0
      ? effectiveLayoutHeight - effectiveHeaderHeight
      : undefined;

  const isReviewOrFinalReviewScroll =
    steps[currentStep].key === "reviewAndEditStep" ||
    steps[currentStep].key === "finalReview";
  /** Open: `visualViewport` hysteresis OR promo focus (covers IME animation + unreliable blur). */
  const reviewWebInFlowFooter =
    Platform.OS === "web" &&
    isWebNarrowViewport &&
    steps[currentStep].key === "reviewAndEditStep" &&
    (reviewWebVkOpen || reviewWebFieldFocused);

  const renderReviewGradientFooterBlock = (gradientBackgroundStyle: object) => {
    const feeData = getGradientFeeData();
    const isPoboPromoActive =
      !!isPromocodeApplied &&
      !promotionInfoHasError(option?.PromotionInfo) &&
      promotionInfoIsPobo(option?.PromotionInfo);
    const showPoboExchangeCompare =
      isPoboPromoActive &&
      preApplyExchangeRate != null &&
      !Number.isNaN(preApplyExchangeRate) &&
      feeData.exchangeRate > 0 &&
      Number(preApplyExchangeRate.toFixed(4)) !==
        Number(feeData.exchangeRate.toFixed(4));
    return (
      <View
        style={styles.footerShrink}
        onLayout={(e: LayoutChangeEvent) =>
          setFooterHeight(e.nativeEvent.layout.height)
        }
      >
        <GradientBackground style={gradientBackgroundStyle}>
          <View style={styles.gradientContent}>
            <GradientExpandSwipeArea
              isExpanded={isGradientExpand}
              onExpand={() => setIsGradientExpand(true)}
              onCollapse={() => setIsGradientExpand(false)}
              onToggle={() => setIsGradientExpand(!isGradientExpand)}
              handleStyle={styles.viewTap}
            >
            {isGradientExpand && (
              <View style={{ flex: 1 }}>
                <View style={styles.gradientRow}>
                  <Text style={styles.gradientFeeLabel}>
                    {t("EXHANGE_RATE")}
                  </Text>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "baseline",
                      flexShrink: 1,
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    {showPoboExchangeCompare ? (
                      <>
                        <Text
                          style={[
                            styles.gradientFeeLabel,
                            {
                              textDecorationLine: "line-through",
                              marginRight: 8,
                            },
                          ]}
                        >
                          1 {feeData.sendCurrency} ={" "}
                          {preApplyExchangeRate.toFixed(4)}{" "}
                          {feeData.receiveCurrency}
                        </Text>
                        <Text
                          style={[
                            styles.gradientFeeLabel,
                            { color: Colors.successInfoGreen },
                          ]}
                        >
                          1 {feeData.sendCurrency} ={" "}
                          {feeData.exchangeRate.toFixed(4)}{" "}
                          {feeData.receiveCurrency}
                        </Text>
                      </>
                    ) : (
                      <Text style={styles.gradientFeeLabel}>
                        1 {feeData.sendCurrency} ={" "}
                        {feeData.exchangeRate.toFixed(4)}{" "}
                        {feeData.receiveCurrency}
                      </Text>
                    )}
                  </View>
                </View>
                <View style={styles.gradientRow}>
                  <Text style={styles.gradientFeeLabel}>
                    {t("FEES")}
                  </Text>
                  <View
                    style={{ flexDirection: "row", alignItems: "baseline" }}
                  >
                    {isPromocodeApplied &&
                    !isPoboPromoActive &&
                    !promotionInfoHasError(option?.PromotionInfo) ? (
                      <>
                        <Text
                          style={[
                            styles.gradientFeeLabel,
                            {
                              textDecorationLine: "line-through",
                              marginRight: 8,
                            },
                          ]}
                        >
                          {preApplyFees?.toFixed(2)}{" "}
                          {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}
                        </Text>
                        <Text
                          style={[
                            styles.gradientFeeLabel,
                            { color: Colors.successInfoGreen },
                          ]}
                        >
                          {postApplyFees?.toFixed(2)}{" "}
                          {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}
                        </Text>
                      </>
                    ) : (
                      <Text style={styles.gradientFeeLabel}>
                        {summary.fees.toFixed(2)}{" "}
                        {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}
                      </Text>
                    )}
                  </View>
                </View>
                <View style={styles.gradientRow}>
                  <Text style={styles.gradientFeeLabel}>
                    {t("US_FED_REMITTANCE_TAX")}
                  </Text>
                  <Text style={styles.gradientFeeLabel}>
                    {feeData.totalSendTax.toFixed(2)} {feeData.sendCurrency}
                  </Text>
                </View>
              </View>
            )}
            <View style={[styles.gradientRow, { marginBottom: 16 }]}>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <WarninBlackIcon width={16} height={16} />
                <Text style={[styles.gradientPrice, { marginLeft: 8 }]}>
                  {t("REVIEW_TOTAL")}
                </Text>
              </View>
              <Text style={styles.gradientPrice}>
                {feeData.total.toFixed(2)} {feeData.sendCurrency}
              </Text>
            </View>
            </GradientExpandSwipeArea>
            <TouchableOpacity
              testID="gradientContinueButton"
              onPressIn={() => {
                if (
                  Platform.OS === "web" &&
                  isWebNarrowViewport &&
                  steps[currentStep]?.key === "reviewAndEditStep"
                ) {
                  setReviewWebFieldFocused(false);
                }
              }}
              onPress={() => handleNext()}
              activeOpacity={0.8}
              style={styles.gradientContinueButton}
            >
              <Text style={styles.gradientContinueButtonText}>
                {steps[currentStep].key === "finalReview"
                  ? t("STRKEY_SUBMIT")
                  : t("STRKEY_CONTINUE")}
              </Text>
            </TouchableOpacity>
          </View>
        </GradientBackground>
      </View>
    );
  };

  return (
    <KeyboardAvoidingView
      style={[
        styles.container,
        /* Web: height from `effectiveLayoutHeight` — visual viewport for single-field steps, full window for multi-field. */
        Platform.OS === "web" && effectiveLayoutHeight > 0
          ? {
              height: effectiveLayoutHeight,
              minHeight: effectiveLayoutHeight,
              maxHeight: effectiveLayoutHeight,
            }
          : effectiveLayoutHeight > 0 && { height: effectiveLayoutHeight },
      ]}
      behavior={
        Platform.OS === "web"
          ? undefined
          : stickFooterAboveKeyboard
            ? "padding"
            : undefined
      }
      enabled={Platform.OS === "web" ? false : stickFooterAboveKeyboard}
      keyboardVerticalOffset={
        Platform.OS === "ios" && stickFooterAboveKeyboard ? 60 : 0
      }
    >
      <View style={styles.mainWrapper}>
        {steps[currentStep].key !== "pinCreated" && (
          <>
            <View
              style={[
                styles.header,
                Platform.OS === "web" && styles.headerFixed,
              ]}
              onLayout={(e: LayoutChangeEvent) =>
                setHeaderHeight(e.nativeEvent.layout.height)
              }
            >
              <View style={styles.topBar}>
                <View
                  style={[
                    styles.topBarSide,
                    steps[currentStep].key === "headToCashier" &&
                      styles.topBarSideHidden,
                  ]}
                >
                  {steps[currentStep].key !== "headToCashier" ? (
                    <TouchableOpacity
                      style={styles.topBarButton}
                      onPress={handleBack}
                      testID="backButton"
                    >
                      <BackIcon width={24} height={24} />
                    </TouchableOpacity>
                  ) : null}
                </View>
                <View style={styles.topBarCenter}>
                  {steps[currentStep].key === "sendingAmountEntry" ? (
                    <TouchableOpacity
                      style={styles.topBarCountry}
                      onPress={handleCountryEdit}
                      testID="countryButton"
                    >
                      <View style={styles.topBarCountryFlag}>
                        <Flag
                          iso3={selectedDestinationCountryCode ?? ""}
                          width={16}
                          height={16}
                        />
                      </View>
                      <Text
                        style={styles.selectedCountryText}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {selectedCountryName}
                      </Text>
                      <View style={styles.topBarCountryChevron}>
                        <DownArrow width={16} height={16} />
                      </View>
                    </TouchableOpacity>
                  ) : null}
                </View>
                <View style={styles.topBarActions}>
                  <TouchableOpacity
                    onPress={() => setNeedHelp(true)}
                    style={styles.topBarButton}
                    testID="helpButton"
                  >
                    <HelpIcon width={24} height={24} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.topBarButton}
                    onPress={handleClose}
                    testID="closeButton"
                  >
                    <CloseIcon width={24} height={24} />
                  </TouchableOpacity>
                </View>
              </View>
              <View style={styles.progressRow}>
                {steps.map((_, i) => (
                  <View
                    key={i}
                    style={[
                      styles.progressSegment,
                      { borderTopLeftRadius: i === 0 ? 9 : 0 },
                      { borderBottomLeftRadius: i === 0 ? 9 : 0 },
                      { borderTopRightRadius: i === steps.length - 1 ? 9 : 0 },
                      {
                        borderBottomRightRadius: i === steps.length - 1 ? 9 : 0,
                      },
                      {
                        backgroundColor:
                          i <= currentStep
                            ? Colors.titleTextColor
                            : Colors.searchBackground,
                      },
                    ]}
                  />
                ))}
              </View>
            </View>
            {Platform.OS === "web" && (
              <View
                style={[
                  styles.headerSpacer,
                  headerHeight > 0 && { height: headerHeight },
                ]}
              />
            )}
          </>
        )}
        {steps[currentStep].key === "country" ||
        steps[currentStep].key === "state" ||
        steps[currentStep].key === "stateOfId" ||
        steps[currentStep].key === "howTheyReceive" ||
        steps[currentStep].key === "receiverName" ||
        steps[currentStep].key === "senderName" ||
        steps[currentStep].key === "senderAddress" ||
        steps[currentStep].key === "sendingAmountEntry" ? (
          (() => {
            const stepAreaHeight =
              steps[currentStep].key === "howTheyReceive" ||
              steps[currentStep].key === "receiverName" ||
              steps[currentStep].key === "senderName" ||
              steps[currentStep].key === "senderAddress" ||
              steps[currentStep].key === "sendingAmountEntry"
                ? scrollAreaHeight
                : countryStateAreaHeight;
            const hasHeight = stepAreaHeight != null && stepAreaHeight > 0;
            return (
              <View
                style={[
                  styles.stepAreaContainer,
                  hasHeight && {
                    height: stepAreaHeight,
                    maxHeight: stepAreaHeight,
                  },
                  !hasHeight &&
                    Platform.OS === "web" &&
                    styles.stepAreaContainerWebFallback,
                ]}
              >
                {steps[currentStep].key === "country" ? (
                  <AnyStepComponent
                    testID="step-country"
                    onCountrySelected={(code?: string) => handleNext(code)}
                  />
                ) : steps[currentStep].key === "state" ? (
                  <AnyStepComponent
                    testID="step-state"
                    onCountrySelected={(code?: string) => handleNext(code)}
                  />
                ) : steps[currentStep].key === "stateOfId" ? (
                  <AnyStepComponent
                    testID="step-stateOfId"
                    onCountrySelected={(code?: string) => handleNext(code)}
                  />
                ) : steps[currentStep].key === "howTheyReceive" ? (
                  <AnyStepComponent
                    testID="step-howTheyReceive"
                    onCountrySelected={handleNext}
                    onValidationChange={handleValidationChange}
                    restorePreviousSelection={!!returnToStep}
                  />
                ) : steps[currentStep].key === "senderName" ? (
                  <AnyStepComponent
                    testID="step-senderName"
                    ref={SenderFullNameStepRef}
                    onValidationChange={handleValidationChange}
                    onContinue={handleNext}
                  />
                ) : steps[currentStep].key === "senderAddress" ? (
                  <AnyStepComponent
                    testID="step-senderAddress"
                    ref={SenderAddressStepRef}
                    onValidationChange={handleValidationChange}
                    onContinue={handleNext}
                  />
                ) : steps[currentStep].key === "sendingAmountEntry" ? (
                  <AnyStepComponent
                    testID="step-sendingAmountEntry"
                    onValidationChange={handleValidationChange}
                    onContinue={handleNext}
                    cameFromEdit={
                      !!(
                        returnToStep &&
                        (returnToStep === "reviewAndEditStep" ||
                          returnToStep === "finalReview")
                      )
                    }
                  />
                ) : (
                  <AnyStepComponent
                    testID="step-receiverName"
                    ref={ReceiverFullNameStepRef}
                    onValidationChange={handleValidationChange}
                    onContinue={handleNext}
                  />
                )}
              </View>
            );
          })()
        ) : (
          <ScrollView
            ref={scrollRef}
            style={[
              styles.scrollView,
              scrollAreaHeight != null &&
                scrollAreaHeight > 0 && { maxHeight: scrollAreaHeight },
              isReviewOrFinalReviewScroll && { backgroundColor: Colors.white },
            ]}
            contentContainerStyle={
              isReviewOrFinalReviewScroll
                ? {
                    paddingBottom: reviewWebInFlowFooter
                      ? 24
                      : isGradientExpand
                        ? 340
                        : 220,
                    backgroundColor: Colors.white,
                  }
                : steps[currentStep].key === "headToCashier"
                  ? styles.scrollContainerHeadToCashier
                  : steps[currentStep].key === "fraudPrevention"
                    ? styles.scrollContainerFraudPrevention
                    : Platform.OS === "web" && {
                        paddingBottom: footerHeight || 100,
                      }
            }
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            bounces={
              !(
                steps[currentStep].key === "reviewAndEditStep" ||
                steps[currentStep].key === "finalReview"
              )
            }
          >
            {steps[currentStep].key === "phone" ? (
              <AnyStepComponent
                testID="step-phone"
                ref={phoneStepRef}
                onValidationChange={handleValidationChange}
              />
            ) : steps[currentStep].key === "senderDOB" ? (
              <AnyStepComponent
                testID="step-senderDOB"
                ref={SenderDOBStepRef}
                onValidationChange={handleValidationChange}
              />
            ) : steps[currentStep].key === "senderIdNumber" ? (
              <AnyStepComponent
                testID="step-senderIdNumber"
                ref={SenderIdentificationStepRef}
                onValidationChange={handleValidationChange}
              />
            ) : steps[currentStep].key === "senderIdentification" ? (
              <AnyStepComponent
                testID="step-senderIdentification"
                onContinue={handleNext}
                onValidationChange={handleValidationChange}
              />
            ) : steps[currentStep].key === "reviewAndEditStep" ||
              steps[currentStep].key === "finalReview" ? (
              <>
                <AnyStepComponent
                  ref={
                    steps[currentStep].key === "reviewAndEditStep"
                      ? ReviewAndEditStepRef
                      : undefined
                  }
                  testID={
                    steps[currentStep].key === "reviewAndEditStep"
                      ? "step-reviewAndEditStep"
                      : "step-finalReview"
                  }
                  onEditStep={handleEditStep}
                  onContinue={handleNext}
                  handleApplyPromoCode={handleApplyPromoCode}
                  updatePromocode={updatePromocode}
                  clearPromoCode={clearPromoCode}
                  promoCode={promoCode}
                  errorText={errors.promoCode}
                  selectionError={
                    steps[currentStep].key === "reviewAndEditStep"
                      ? (reviewStepApiError ?? undefined)
                      : (finalReviewApiError ?? undefined)
                  }
                  onWebEditableFocusChange={
                    Platform.OS === "web" &&
                    isWebNarrowViewport &&
                    steps[currentStep].key === "reviewAndEditStep"
                      ? setReviewWebFieldFocused
                      : undefined
                  }
                />
                {reviewWebInFlowFooter
                  ? renderReviewGradientFooterBlock(
                      styles.gradientFooterScrollableWeb,
                    )
                  : null}
              </>
            ) : steps[currentStep].key === "fraudPrevention" ? (
              <AnyStepComponent
                testID="step-fraudPrevention"
                onContinue={handleNext}
              />
            ) : steps[currentStep].key === "otp" ? (
              <AnyStepComponent testID="step-otp" onContinue={handleNext} />
            ) : steps[currentStep].key === "pin" ||
              steps[currentStep].key === "confirmPin" ? (
              (() => {
                const isResetPinFlow = steps.some((s) => s.key === "otp");
                // In reset PIN flow, PIN step is for entering new PIN (not verification)
                if (isResetPinFlow && steps[currentStep].key === "pin") {
                  return (
                    <AnyStepComponent
                      testID="step-pin-reset"
                      attemptsLeft={undefined}
                      locked={false}
                      showAttemptsWarning={false}
                      resetSignal={resetSignal}
                      onContinue={handleNext}
                      apiError={pinStepApiError ?? undefined}
                    />
                  );
                }
                // Normal PIN step (verification or new consumer)
                return (
                  <AnyStepComponent
                    testID={
                      steps[currentStep].key === "pin"
                        ? "step-pin"
                        : "step-confirmPin"
                    }
                    handleForgotPassword={handleForgotPassword}
                    attemptsLeft={attemptsLeft}
                    locked={locked}
                    showAttemptsWarning={showPinAttemptsWarning}
                    resetSignal={resetSignal}
                    onContinue={handleNext}
                    apiError={
                      steps[currentStep].key === "pin"
                        ? (pinStepApiError ?? undefined)
                        : (confirmPinApiError ?? undefined)
                    }
                  />
                );
              })()
            ) : steps[currentStep].key === "pinCreated" ? (
              <AnyStepComponent testID="step-pinCreated" />
            ) : steps[currentStep].key === "headToCashier" ? (
              <AnyStepComponent testID="step-headToCashier" />
            ) : steps[currentStep].key === "additionalInformation" ? (
              <AnyStepComponent
                ref={AdditionalInformationStepRef}
                testID="step-additionalInformation"
                onValidationChange={handleValidationChange}
              />
            ) : null}
          </ScrollView>
        )}
        <Modal
          visible={needHelp}
          transparent
          animationType="none"
          onRequestClose={() => closeNeedHelp?.()}
        >
          <NeedHelpComponent
            closeNeedHelp={closeNeedHelp}
            handleNeedHelpCall={handleNeedHelpCall}
          />
        </Modal>
        <Modal
          visible={isDOBConfirm}
          transparent
          animationType="none"
          onRequestClose={() => setIsDOBCOnfirm(false)}
        >
          {renderDOBPopUp()}
        </Modal>
        <Modal
          visible={isReceiverFullnameConfirm}
          transparent
          animationType="none"
          onRequestClose={() => setIsReceiverFullnameConfirm(false)}
        >
          {renderReceiverInfoConfirmationPopUp()}
        </Modal>
        <Modal
          visible={showPopup}
          transparent
          animationType="none"
          onRequestClose={() => setShowPopup(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.bottomSheet}>
              <TouchableOpacity
                onPress={() => setShowPopup(false)}
                style={styles.modalHeaderRow}
                onFocus={(e: any) => e?.target?.blur?.()}
              >
                <CloseIcon width={24} height={24} />
              </TouchableOpacity>
              <View style={{ alignItems: "center" }}>
                <AccountLockedIcon width={64} height={64} />
                <Text style={styles.accountLock}>
                  {t("YOUR_ACCOUNT_LOCKED")}
                </Text>
                <Text style={styles.accountLockInfo}>
                  <Trans
                    i18nKey="YOU_CAN_CONTINUE_AS_GUEST"
                    components={{
                      guest: (
                        <Text
                          style={[
                            styles.accountLockInfo,
                            { fontWeight: "700" },
                          ]}
                        />
                      ),
                    }}
                  />
                </Text>
              </View>
              <TouchableOpacity
                style={styles.button}
                testID="continueAsGuestButton"
                onPress={() => handleForgotPassword?.()}
                onFocus={(e: any) => e?.target?.blur?.()}
              >
                <Text style={styles.buttonText}>{t("CONTINUE_AS_GUEST")}</Text>
              </TouchableOpacity>
              {/* <TouchableOpacity
                testID="resetPinButton"
                style={styles.cancelBtn}
                onPress={handleResetPin}
                onFocus={(e: any) => e?.target?.blur?.()}
              >
                <Text style={[styles.cancelText, { lineHeight: 24 }]}>
                  {t("RESET_PIN")}
                </Text>
              </TouchableOpacity> */}
            </View>
          </View>
        </Modal>
        {steps[currentStep].key !== "country" &&
          steps[currentStep].key !== "state" &&
          steps[currentStep].key !== "stateOfId" &&
          steps[currentStep].key !== "howTheyReceive" &&
          steps[currentStep].key !== "senderIdentification" &&
          steps[currentStep].key !== "reviewAndEditStep" &&
          steps[currentStep].key !== "finalReview" &&
          steps[currentStep].key !== "receiverName" &&
          steps[currentStep].key !== "senderName" &&
          steps[currentStep].key !== "senderAddress" &&
          steps[currentStep].key !== "sendingAmountEntry" && (
            <View
              style={[
                styles.footerContainer,
                styles.footerShrink,
                Platform.OS === "web" &&
                  !stickFooterAboveKeyboard &&
                  styles.footerContainerWebViewportPinned,
                (steps[currentStep].key === "pin" ||
                  steps[currentStep].key === "confirmPin") &&
                  styles.footerContainerPinSteps,
              ]}
              onLayout={(e: LayoutChangeEvent) =>
                setFooterHeight(e.nativeEvent.layout.height)
              }
            >
              {steps[currentStep].key !== "pin" &&
                steps[currentStep].key !== "confirmPin" && (
                  <TouchableOpacity
                    testID="continueButton"
                    style={[
                      styles.button,
                      { backgroundColor: Colors.secondaryText },
                    ]}
                    onPress={() => handleNext()}
                  >
                    <Text style={styles.buttonText}>
                      {steps[currentStep].key === "additionalInformation" ||
                      currentStep !== steps.length - 1
                        ? t("STRKEY_CONTINUE")
                        : t("I_AM_FINISHED")}
                    </Text>
                  </TouchableOpacity>
                )}
              {(steps[currentStep].key === "pin" ||
                steps[currentStep].key === "confirmPin") &&
                !steps.some((s) => s.key === "otp") && (
                  <TouchableOpacity
                    onPress={
                      steps === BASE_STEPS_EXISTING
                        ? handleForgotPassword
                        : handleSkip
                    }
                    style={styles.secondButton}
                    testID="continueAsGuestButton"
                  >
                    <Text style={styles.contniueAsGuest}>
                      {steps === BASE_STEPS_EXISTING
                        ? t("CONTINUE_AS_GUEST")
                        : t("SKIP")}
                    </Text>
                  </TouchableOpacity>
                )}
            </View>
          )}
        {(steps[currentStep].key === "reviewAndEditStep" ||
          steps[currentStep].key === "finalReview") &&
          !(
            steps[currentStep].key === "reviewAndEditStep" &&
            reviewWebInFlowFooter
          ) &&
          renderReviewGradientFooterBlock(styles.gradientFooter)}
        {steps[currentStep].key === "sendingAmountEntry" && (
          <View
            style={styles.footerShrink}
            onLayout={(e: LayoutChangeEvent) =>
              setFooterHeight(e.nativeEvent.layout.height)
            }
          >
            <GradientBackground style={styles.gradientFooter}>
              <View style={styles.gradientContent}>
                <View style={[styles.gradientRow, { marginTop: 24 }]}>
                  <Text
                    style={[
                      styles.gradientPrice,
                      { textDecorationLine: "underline" },
                    ]}
                  >
                    {t("REVIEW_TOTAL")}:
                  </Text>
                  <Text style={styles.gradientPrice}>
                    {(() => {
                      const amountNum = Number(
                        String(currentSendAmount || "0").replace(/,/g, ""),
                      );
                      const formatted = isFinite(amountNum)
                        ? amountNum.toFixed(2)
                        : "0.00";
                      return `${formatted} USD`;
                    })()}
                  </Text>
                </View>
                <TouchableOpacity
                  testID="continueButton"
                  onPress={() => handleNext()}
                  activeOpacity={0.8}
                  style={styles.gradientContinueButton}
                >
                  <Text style={styles.gradientContinueButtonText}>
                    {t("STRKEY_CONTINUE")}
                  </Text>
                </TouchableOpacity>
              </View>
            </GradientBackground>
          </View>
        )}
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    backgroundColor: Colors.white,
    ...(Platform.OS === "web" && {
      // dvh tracks mobile browser chrome better than vh (iOS Safari URL bar)
      minHeight: "100dvh" as any,
      height: "100dvh" as any,
      maxHeight: "100dvh" as any,
      overflow: "hidden" as any,
    }),
  },
  header: {
    paddingHorizontal: 24,
    flexShrink: 0,
  },
  headerFixed: {
    ...(Platform.OS === "web" && {
      position: "fixed" as any,
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      backgroundColor: Colors.white,
    }),
  },
  headerSpacer: {
    ...(Platform.OS === "web" && {
      height: 80, // fallback until header onLayout runs
      width: "100%",
    }),
  },
  content: {
    flex: 1,
    // width: "100%",
  },
  scrollView: {
    flex: 1,
    ...(Platform.OS === "web" && {
      minHeight: 0 as any,
      overflow: "auto" as any,
      overflowX: "hidden" as any,
    }),
  },
  /** Step area when step owns its scroll (country, state, stateOfId, howTheyReceive). No overflow:auto so inner list scrolls. */
  stepAreaContainer: {
    flex: 1,
    minHeight: 0,
    ...(Platform.OS === "web" && {
      overflow: "hidden" as any,
      overflowX: "hidden" as any,
    }),
  },
  /** When stepAreaHeight is not yet available (e.g. footer not measured), still constrain height on web so inner scroll works. */
  stepAreaContainerWebFallback: {
    height: "100%" as any,
    minHeight: "50vh" as any,
  },
  // scrollContainer: {
  //   paddingBottom: footerHeight || 100, // space for footer (no flexGrow to avoid unnecessary scroll when content is short)
  // },
  scrollContainerFraudPrevention: {
    paddingBottom: 32, // only 32px after content before footer
  },
  scrollContainerHeadToCashier: {
    paddingBottom: 32, // 32px space after HeadToCashier content before "I'm finished" button
  },
  // Review/final steps: paddingBottom is now computed dynamically from gradientFooterHeight
  mainWrapper: {
    flex: 1,
    flexDirection: "column",
    ...(Platform.OS === "web" && {
      minHeight: 0 as any,
      overflow: "hidden" as any,
    }),
  },
  footerContainer: {
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
    paddingTop: 14,
    /** 24px under Continue; web adds safe-area so inset-only does not replace the 24px. */
    ...(Platform.OS === "web"
      ? ({
          paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
        } as any)
      : { paddingBottom: 24 }),
  },
  /**
   * Web multi-field steps: pin Continue to the layout viewport bottom (does not ride up with the keyboard).
   * Single-field steps omit this — footer stays in column flow at the bottom of the visualViewport-sized shell.
   */
  footerContainerWebViewportPinned: {
    position: "fixed" as any,
    left: 0,
    right: 0,
    width: "100%",
    zIndex: 999,
    bottom: 0,
  },
  /** Skip / Continue as guest on pin steps: 16px to device / keyboard bottom (not full footer inset). */
  footerContainerPinSteps: {
    paddingBottom: 16,
  },
  footerShrink: {
    flexShrink: 0,
  },
  dropdownPriorityScrollLayer: {
    ...(Platform.OS === "web" && {
      position: "relative" as any,
      zIndex: 5,
      overflow: "visible" as any,
    }),
  },
  footerBehindDropdownLayer: {
    ...(Platform.OS === "web" && {
      position: "relative" as any,
      zIndex: 1,
    }),
  },
  button: {
    backgroundColor: Colors.black,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 14,
    marginBottom: 0, // 24px gap comes from footerContainer paddingBottom
  },
  secondButton: {
    backgroundColor: Colors.white,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginBottom: 0,
  },
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    paddingVertical: 16,
    gap: 8,
  },
  topBarSide: {
    width: 48,
    flexShrink: 0,
    alignItems: "flex-start",
    justifyContent: "center",
  },
  topBarSideHidden: {
    width: 0,
    minWidth: 0,
    overflow: "hidden",
  },
  topBarCenter: {
    flex: 1,
    minWidth: 0,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 4,
  },
  topBarActions: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
    gap: 16,
  },
  topBarButton: {
    padding: 1,
  },
  topBarCountry: {
    flexDirection: "row",
    alignItems: "center",
    maxWidth: "100%",
    flexShrink: 1,
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
  },
  topBarCountryFlag: {
    flexShrink: 0,
    marginRight: 6,
  },
  topBarCountryChevron: {
    flexShrink: 0,
    marginLeft: 4,
  },
  selectedCountryText: {
    flex: 1,
    minWidth: 0,
    fontFamily: "Rubik",
    fontSize: 12,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 16,
  },
  IconStyle: {
    height: 24,
    width: 24,
  },
  iconText: {
    fontSize: 20,
    color: "#111",
  },
  progressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    height: 4,
  },
  progressSegment: {
    flex: 1,
    height: 4,
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 16,
  },
  buttonText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "600",
    color: Colors.white,
    lineHeight: 20,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  root: {
    flex: 1,
    justifyContent: "flex-end",
    position: "fixed" as any, // web only
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.4)",
  },

  modalContent: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    zIndex: 2, // ensures it stays above scrim
  },
  // modalContent: {
  //   backgroundColor: "white",
  //   padding: 20,
  //   borderTopLeftRadius: 20,
  //   borderTopRightRadius: 20,
  // },
  accountLock: {
    fontSize: 24,
    fontWeight: "500",
    lineHeight: 28,
    fontFamily: "Rubik",
    marginVertical: 26,
    color: Colors.accountLock,
  },
  accountLockInfo: {
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 18,
    fontFamily: 'Rubik',
    marginBottom: 48,
    color: Colors.accountLock,
    textAlign: "center",
  },
  skipButton: {
    paddingVertical: 16,
    alignItems: "center",
  },
  skipText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.darkGrey,
    lineHeight: 24,
  },
  modalHeaderRow: {
    alignSelf: "flex-end",
    marginBottom: 48,
  },
  modalTitle: {
    fontFamily: "Rubik",
    fontSize: 24,
    fontWeight: "500",
    color: Colors.accountLock,
    lineHeight: 28,
    textAlign: "center",
    marginVertical: 26,
  },
  modalInfo: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "400",
    color: Colors.accountLock,
    lineHeight: 18,
    marginBottom: 26,
    textAlign: "center",
    // paddingHorizontal: 10
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalDOB: {
    fontFamily: "Rubik",
    fontSize: 32,
    fontWeight: "500",
    color: Colors.accountLock,
    textAlign: "center",
    lineHeight: 38,
    marginBottom: 48,
  },
  /** Full-width so long first/last names wrap; centered with sheet horizontal padding */
  receiverLegalNameContainer: {
    alignSelf: "stretch",
    width: "100%",
    paddingHorizontal: 8,
    marginBottom: 48,
  },
  receiverLegalNameText: {
    fontFamily: "Rubik",
    fontSize: 32,
    fontWeight: "500",
    color: Colors.accountLock,
    textAlign: "center",
    width: "100%",
  },
  confirmBtn: {
    backgroundColor: Colors.titleTextColor,
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    height: 48,
  },
  footer: {
    // paddingHorizontal: 24,
    paddingBottom: 24,
    backgroundColor: Colors.white,
  },
  cancelBtn: { paddingTop: 24, alignItems: "center" },
  cancelText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.darkGrey,
    lineHeight: 20,
  },
  contniueAsGuest: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "500",
    lineHeight: 20,
    color: Colors.titleTextColor,
  },
  gradientFooter: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    ...(Platform.OS === "web" && {
      position: "fixed" as any,
      bottom: 0,
      left: 0,
      right: 0,
      width: "100%" as any,
      margin: 0 as any,
      boxSizing: "border-box" as any,
      // Prevent gap above browser chrome on iPhone Safari / mobile
      paddingBottom: "env(safe-area-inset-bottom, 0px)" as any,
    }),
  },
  /** Web review step: same chrome as gradientFooter but in document flow (inside ScrollView) while a field is focused. */
  gradientFooterScrollableWeb: {
    width: "100%" as any,
    alignSelf: "stretch" as any,
    marginTop: 24,
    zIndex: 1,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingBottom: "env(safe-area-inset-bottom, 0px)" as any,
  } as any,
  viewTap: {
    height: 4,
    width: 77,
    backgroundColor: Colors.titleTextColor,
    borderRadius: 24,
    // marginBottom: 24,
    alignSelf: "center",
  },
  gradientContent: {
    paddingHorizontal: 24,
    // paddingVertical: 20,
    // alignItems: "center",
  },
  gradientRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 8,
  },
  gradientFeeLabel: {
    fontSize: 14,
    fontFamily: "Rubik",
    fontWeight: "400",
    lineHeight: 22,
    color: Colors.titleTextColor,
  },
  gradientPrice: {
    fontSize: 16,
    fontFamily: "Rubik",
    fontWeight: "500",
    lineHeight: 24,
    color: Colors.titleTextColor,
  },
  gradientContinueButton: {
    width: "100%",
    backgroundColor: Colors.titleTextColor,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center",
    shadowColor: "#000",
    marginBottom: 32,
  },
  gradientContinueButtonText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.white,
  },
  oldPrice: {
    fontSize: 16,
    fontFamily: "Rubik",
    // textDecorationLine: "line-through",
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontFamily: "Rubik",
    fontWeight: "bold",
    color: "green",
  },
});
